import L from "leaflet";
import * as turf from "@turf/turf";
import { reportTaskStatus } from "../../core/task-status.js";
import { ASPRS_CLASSES, asprsClassName, classDefaultColor } from "../../core/asprs-classes.js";

// ── Colormaps ─────────────────────────────────────────────────────────────────

const COLORMAPS = [
  { value: "blues_r",        label: "Blues - White"   },
  { value: "viridis",        label: "Viridis"         },
  { value: "turbo",          label: "Turbo"           },
  { value: "magma",          label: "Magma"           },
  { value: "rdylbu_r",       label: "Red-Blue"        },
  { value: "gray",           label: "Grayscale"       },
  { value: "pastel_rainbow", label: "Pastel Rainbow"  },
  { value: "earth",          label: "Earth"           },
  { value: "polar",          label: "Polar"           },
  { value: "copper",         label: "Copper"          },
  { value: "solid",          label: "Custom"          },
];

const COLORMAP_STOPS = {
  blues_r:        [[0,[8,48,107]],[0.4,[33,113,181]],[0.7,[189,215,231]],[1,[255,255,255]]],
  viridis:        [[0,[68,1,84]],[0.25,[59,82,139]],[0.5,[33,145,140]],[0.75,[94,201,98]],[1,[253,231,37]]],
  turbo:          [[0,[35,23,27]],[0.25,[68,118,247]],[0.5,[66,211,164]],[0.75,[212,239,7]],[1,[144,12,0]]],
  magma:          [[0,[0,0,4]],[0.25,[81,18,124]],[0.5,[190,60,80]],[0.75,[251,176,100]],[1,[252,253,191]]],
  rdylbu_r:       [[0,[49,54,149]],[0.25,[171,217,233]],[0.5,[255,255,191]],[0.75,[253,174,97]],[1,[165,0,38]]],
  gray:           [[0,[0,0,0]],[1,[255,255,255]]],
  pastel_rainbow: [[0,[180,120,200]],[0.17,[130,150,230]],[0.33,[100,200,200]],[0.5,[130,210,150]],[0.67,[220,220,120]],[0.83,[240,170,120]],[1,[230,140,150]]],
  earth:          [[0,[15,40,110]],[0.2,[25,100,150]],[0.38,[200,170,90]],[0.5,[100,160,80]],[0.7,[140,110,65]],[0.85,[170,155,135]],[1,[230,230,225]]],
  polar:          [[0,[30,60,180]],[0.25,[120,170,220]],[0.5,[255,255,255]],[0.75,[220,130,100]],[1,[180,30,30]]],
  copper:         [[0,[0,0,0]],[0.4,[85,35,5]],[0.75,[175,95,40]],[1,[255,175,85]]],
};

function getColormapColor(t, name) {
  const stops = COLORMAP_STOPS[name] || COLORMAP_STOPS.blues_r;
  t = Math.max(0, Math.min(1, t));
  let lo = stops[0], hi = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (t >= stops[i][0] && t <= stops[i + 1][0]) { lo = stops[i]; hi = stops[i + 1]; break; }
  }
  const span = hi[0] - lo[0];
  const f = span < 1e-10 ? 0 : (t - lo[0]) / span;
  return [0, 1, 2].map(j => Math.round(lo[1][j] + f * (hi[1][j] - lo[1][j])));
}

function colormapGradientCss(name) {
  if (name === "solid") return "linear-gradient(to right,#dc3232,#38bdf8,#22c55e)";
  const stops = COLORMAP_STOPS[name];
  if (!stops) return "#888";
  return `linear-gradient(to right,${stops.map(([t,[r,g,b]]) => `rgb(${r},${g},${b}) ${(t*100).toFixed(0)}%`).join(",")})`;
}

function rgbToHex(rgb) { return "#" + rgb.map(v => v.toString(16).padStart(2,"0")).join(""); }
function hexToRgb(hex) { const s = hex.replace("#",""); return [0,2,4].map(i => parseInt(s.slice(i,i+2),16)); }
function esc(v) { return String(v??"").replaceAll("&","&amp;").replaceAll("<","&lt;").replaceAll(">","&gt;").replaceAll('"',"&quot;"); }
function uid() { return Math.random().toString(36).slice(2)+Date.now().toString(36); }

function barLabelLeft(pct) {
  if (pct <= 0) return "0%";
  if (pct >= 100) return "100%";
  return `${pct.toFixed(2)}%`;
}
function barLabelTransform(pct) {
  if (pct <= 0) return "none";
  if (pct >= 100) return "translateX(-100%)";
  return "translateX(-50%)";
}

const DEFAULT_SETTINGS = () => ({
  displayUnit: "ft",
  colormapName: "blues_r",
  rescaleMin: null,
  rescaleMax: null,
  underflowColor: [45,55,72],
  overflowColor: [45,55,72],
  underflowTransparent: false,
  overflowTransparent: false,
  tileOpacity: 1.0,
  hillshadeOverlay: true,
  customBins: 3,
  customColors: [[220,50,50],[56,189,248],[34,197,94]],
  customDividers: null,
  customTransparent: [],
  hillshadeAzimuth: 315,
  hillshadeAltitude: 45,
  tileZFactor: 1.0,
  tileGamma: 1.0,
  tileSaturation: 1.0,
  tileContrast: 1.0,
  tileResampling: "bilinear",
  classStyles: {}, // classification mosaics only: { [classId]: { visible, color } } overrides
  displayBand: "mean", // which of entry.bands (multi-band surfaces only) is currently rendered
});

// Grid statistics PDAL's writers.gdal can bin per cell, in the fixed order it always emits
// bands in regardless of the order they're requested — see SURFACE_OUTPUT_TYPES server-side
// (services/ais-api/src/server.js), which this mirrors so band index stays in sync.
const SURFACE_BAND_STATS = [
  { key: "min", label: "Min" },
  { key: "max", label: "Max" },
  { key: "mean", label: "Mean" },
  { key: "idw", label: "IDW" },
  { key: "count", label: "Count" },
  { key: "stdev", label: "Std Dev" },
];

// The classification mosaic is categorical — value→color lookups, no stretch. It shares the
// mosaic2d kind (same route, same manifest) but carries attr: "classification"; the elevation
// mosaic predates attrs and stays attr-less.
function isClsMosaic(s) { return s?.kind === "mosaic2d" && s?.attr === "classification"; }

function presentClasses() {
  const classes = mosaicAvailable?.attrs?.classification?.classes;
  return Array.isArray(classes) && classes.length ? classes : [];
}

// Discrete TiTiler colormap from the shared ASPRS palette + per-class user overrides,
// restricted to classes present in the mosaic. Hidden classes are omitted — rio-tiler
// renders unmapped values transparent.
function buildClassificationColormapJson(s) {
  const styles = s.settings.classStyles || {};
  const cm = {};
  for (const id of presentClasses()) {
    const ov = styles[id] || {};
    if (ov.visible === false) continue;
    const [r, g, b] = hexToRgb(ov.color || classDefaultColor(id));
    cm[id] = [r, g, b, 255];
  }
  // All classes hidden: keep the colormap valid but fully transparent (255 is the nodata
  // value, never a real class).
  if (!Object.keys(cm).length) cm[255] = [0, 0, 0, 0];
  return JSON.stringify(cm);
}

// ── Module state ──────────────────────────────────────────────────────────────

let app;
let container = null;
let activeMarinaCode = "";
let catalog = [];
let available = [];
let mosaicAvailable = null; // 2D LiDAR mosaic summary for the active project (null = none/not loaded)
let mosaicProducts = {}; // class-filtered DEM products (attr key -> {label, classFilter}), keyed like mosaicAvailable.attrs
let selectedId = null;
let selectedIds = new Set();
let visibleIds = new Set();
let loadingCatalog = false;
let loadingAvailable = false;
let savePending = false;
let addPickerOpen = false;
let cmPickerOpen = false; // colormap picker dropdown

// Quiet, periodic re-fetch of the catalog so a surface queued by unattended CloudShare
// auto-sync (see remote-storage.js autoSurface) shows up and starts its build-status poll
// without the user having to hit Refresh — mirrors slip-delivery's syncRemoteRecords.
let catalogSyncTimer = null;
let catalogSyncFocusHandler = null;
const CATALOG_SYNC_INTERVAL_MS = 45000;

const surfaceRuntimes = new Map();
const buildPolls = new Map();
const open3dPolls = new Map(); // surfaceId -> setTimeout handle, for the "Open in 3D" button below
let dragSrcId = null;
let closeCmDropdown = null;
let lastMergedHistData = null; // merged histogram across all selected surfaces (multi-select mode)
let rebuildOpen = false;

// ── Colormap ID registry (client-side dedup cache) ────────────────────────────
const cmIdCache  = new Map(); // cmJson → id
const cmIdFlight = new Map(); // cmJson → Promise<id>

async function registerColormap(cmJson) {
  if (cmIdCache.has(cmJson)) return cmIdCache.get(cmJson);
  if (cmIdFlight.has(cmJson)) return cmIdFlight.get(cmJson);
  const p = fetch("/api/qimera/colormap", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ colormap: cmJson }),
  }).then(r => r.json()).then(d => {
    cmIdFlight.delete(cmJson);
    if (d?.id) { cmIdCache.set(cmJson, d.id); return d.id; }
    return null;
  }).catch(() => { cmIdFlight.delete(cmJson); return null; });
  cmIdFlight.set(cmJson, p);
  return p;
}

const surfaceTaskStatus = (msg, pct, running = true) => reportTaskStatus(app, "surfaces", {
  label: "Surfaces", message: msg || "", progress: pct ?? null,
  status: running ? "running" : "idle", projectCode: activeMarinaCode || null,
});
const surfaceTaskIdle = () => surfaceTaskStatus("", null, false);

function getSelected() { return catalog.find(s => s.id === selectedId) || null; }

function applyToAllSelected(fn) {
  for (const id of selectedIds) {
    const s = catalog.find(c => c.id === id);
    if (s) fn(s);
  }
}

// ── S3 persistence ────────────────────────────────────────────────────────────

async function loadCatalog({ quiet = false } = {}) {
  if (!activeMarinaCode) { catalog = []; render(); return; }
  if (!quiet) { loadingCatalog = true; render(); }
  try {
    const r = await fetch(`/api/surfaces/catalog?marina=${encodeURIComponent(activeMarinaCode)}`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = await r.json();
    catalog = Array.isArray(d.catalog) ? d.catalog : [];
    catalog.forEach(s => {
      if (!s.settings) s.settings = DEFAULT_SETTINGS();
      const def = DEFAULT_SETTINGS();
      // back-compat: fill any fields missing from older catalog entries
      for (const k of Object.keys(def)) {
        if (s.settings[k] === undefined) s.settings[k] = def[k];
      }
    });
    catalog.forEach(s => { if (!s.rasterReady) startBuildPoll(s.id); });
    const validIds = new Set(catalog.map(s => s.id));
    for (const id of [...selectedIds]) if (!validIds.has(id)) selectedIds.delete(id);
    if (selectedId && !validIds.has(selectedId)) selectedId = [...selectedIds][0] ?? null;
  } catch {}
  if (!quiet) loadingCatalog = false;
  render();
}

function shouldSkipCatalogSync() {
  return !activeMarinaCode || document.visibilityState === "hidden" || loadingCatalog || savePending;
}

function startCatalogSync() {
  if (!catalogSyncTimer) {
    catalogSyncTimer = setInterval(() => {
      if (!shouldSkipCatalogSync()) loadCatalog({ quiet: true }).catch(() => {});
    }, CATALOG_SYNC_INTERVAL_MS);
  }
  if (!catalogSyncFocusHandler) {
    catalogSyncFocusHandler = () => {
      if (!shouldSkipCatalogSync()) loadCatalog({ quiet: true }).catch(() => {});
    };
    window.addEventListener("focus", catalogSyncFocusHandler);
  }
}

function stopCatalogSync() {
  if (catalogSyncTimer) { clearInterval(catalogSyncTimer); catalogSyncTimer = null; }
  if (catalogSyncFocusHandler) { window.removeEventListener("focus", catalogSyncFocusHandler); catalogSyncFocusHandler = null; }
}

async function saveCatalog() {
  if (!activeMarinaCode) return;
  savePending = true; render();
  try {
    await fetch("/api/surfaces/catalog", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ marina: activeMarinaCode, catalog }),
    });
  } catch {}
  savePending = false; render();
}

async function loadAvailable() {
  if (!activeMarinaCode) { available = []; return; }
  loadingAvailable = true; render();
  try {
    const r = await fetch(`/api/surfaces/available?marina=${encodeURIComponent(activeMarinaCode)}`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = await r.json();
    available = Array.isArray(d.surfaces) ? d.surfaces : [];
  } catch {}
  loadingAvailable = false; render();
}

// The project's 2D LiDAR mosaic (built by the Potree Bulk Generator, styled at render time
// by TiTiler) is offered as an addable "surface" — no build step, it's already rasterized.
async function loadMosaicAvailable() {
  if (!activeMarinaCode) { mosaicAvailable = null; mosaicProducts = {}; return; }
  const code = activeMarinaCode;
  try {
    const [r, rProducts] = await Promise.all([
      fetch(`/api/point-cloud/mosaic2d/${encodeURIComponent(code)}`),
      fetch(`/api/point-cloud/mosaic2d/${encodeURIComponent(code)}/products`),
    ]);
    const d = r.ok ? await r.json() : null;
    const dProducts = rProducts.ok ? await rProducts.json() : null;
    if (code !== activeMarinaCode) return;
    mosaicAvailable = d?.ok && d.mosaic?.tileCount ? d.mosaic : null;
    mosaicProducts = dProducts?.ok && dProducts.products ? dProducts.products : {};
  } catch {
    if (code === activeMarinaCode) { mosaicAvailable = null; mosaicProducts = {}; }
  }
  render();
}

// ── CRS confirmation ──────────────────────────────────────────────────────────
// Every manual build goes through this: the server reads the LAS header (no download) and
// tests whether the CRS about to be used actually puts the file at the marina (see
// checkLasCrs in server.js -- 2026-10-01 HSM was metres labelled as ftUS and landed in PA).
// Resolves to the confirmed EPSG ("" = let the server use the project CRS) or null on cancel.
// Builds sent after this pass crsConfirmed:true so the server doesn't re-check and refuse.
const CRS_ROLE_LABELS = { requested: "entered", project: "project", embedded: "file header", geographic: "lat/lon", candidate: "suggested" };
const CRS_VERDICT_STYLE = {
  ok:         { color: "#4ade80", icon: "✓", title: "CRS confirmed" },
  mismatch:   { color: "#fbbf24", icon: "⚠", title: "CRS doesn't match this marina" },
  "no-match": { color: "#f87171", icon: "✕", title: "No CRS places this file at the marina" },
  unknown:    { color: "#94a3b8", icon: "?", title: "Can't verify CRS" },
};

function confirmLasCrs({ s3Key, marina, sourceEpsg, fileName }) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.style.cssText = "position:fixed;inset:0;z-index:10000;background:rgba(2,6,23,0.6);display:flex;align-items:center;justify-content:center;padding:16px;";
    const panel = document.createElement("div");
    panel.setAttribute("role", "dialog");
    panel.style.cssText = "width:min(560px,100%);max-height:90vh;overflow:auto;background:#0f172a;border:1px solid rgba(148,163,184,0.3);border-radius:8px;color:var(--text,#e2e8f0);font-size:12px;box-shadow:0 20px 50px rgba(0,0,0,0.5);";
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    let last = null;
    const done = (val) => { overlay.remove(); document.removeEventListener("keydown", onKey); resolve(val); };
    const onKey = (e) => { if (e.key === "Escape") done(null); };
    document.addEventListener("keydown", onKey);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) done(null); });

    const bare = (code) => String(code || "").replace(/^EPSG:/i, "");
    const inputSty = "width:80px;font-size:12px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.35);border-radius:3px;color:inherit;padding:3px 6px;";
    const btn = (primary) => `font-size:12px;padding:5px 12px;border-radius:4px;cursor:pointer;border:1px solid ${primary ? "rgba(56,189,248,0.6)" : "rgba(148,163,184,0.3)"};background:${primary ? "rgba(56,189,248,0.18)" : "transparent"};color:inherit;`;
    const fmtDist = (m) => m == null ? "—" : !Number.isFinite(m) ? "invalid" : m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`;

    const paint = (state) => {
      const head = `<div style="padding:12px 14px;border-bottom:1px solid rgba(148,163,184,0.15);display:flex;align-items:center;gap:8px;">
        <div style="font-weight:600;font-size:13px;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">Confirm CRS · ${esc(fileName || s3Key.split("/").pop())}</div>
        <button data-crs-cancel style="background:none;border:none;color:var(--muted,#94a3b8);font-size:16px;cursor:pointer;" title="Cancel">✕</button></div>`;
      if (state.loading) {
        panel.innerHTML = head + `<div style="padding:18px 14px;color:var(--muted,#94a3b8);">Reading the file header and checking it against ${esc(marina)}…</div>`;
      } else if (state.error) {
        panel.innerHTML = head + `<div style="padding:14px;">
          <div style="color:#f87171;margin-bottom:10px;">CRS check failed: ${esc(state.error)}</div>
          <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
            <span style="color:var(--muted,#94a3b8);">Build with EPSG:</span><input data-crs-epsg value="${esc(bare(sourceEpsg))}" placeholder="project" style="${inputSty}" />
            <span style="flex:1"></span><button data-crs-cancel style="${btn(false)}">Cancel</button><button data-crs-build style="${btn(true)}">Build without check</button>
          </div></div>`;
      } else {
        const r = state.result;
        const vs = CRS_VERDICT_STYLE[r.verdict] || CRS_VERDICT_STYLE.unknown;
        const emb = r.embedded;
        const embTxt = emb
          ? `${esc(emb.epsg || "no EPSG code")}${emb.name ? ` · ${esc(emb.name)}` : ""}${emb.horizontalUnit ? ` · ${esc(emb.horizontalUnit)}` : ""}${emb.verticalName || emb.verticalUnit ? ` · vertical ${esc(emb.verticalName || "")} ${esc(emb.verticalUnit || "")}` : ""}`
          : "none embedded";
        const rows = (r.tests || []).filter((t) => !t.roles.every((x) => x === "candidate") || t.code === r.recommended).map((t) => `
          <tr>
            <td style="padding:3px 6px;font-family:monospace;white-space:nowrap;">${esc(t.code || t.srs)}</td>
            <td style="padding:3px 6px;">${esc(t.name || "")}${t.unit ? `<span style="color:var(--muted,#94a3b8);"> · ${esc(t.unit)}</span>` : ""}</td>
            <td style="padding:3px 6px;color:var(--muted,#94a3b8);white-space:nowrap;">${t.roles.map((x) => esc(CRS_ROLE_LABELS[x] || x)).join(", ")}</td>
            <td style="padding:3px 6px;text-align:right;white-space:nowrap;color:${t.match ? "#4ade80" : t.match === false ? "#f87171" : "inherit"};">${t.match ? "✓ " : t.match === false ? "✕ " : ""}${fmtDist(t.distanceM)}</td>
          </tr>`).join("");
        const alts = (r.alternatives || []).filter((a) => a.code !== r.recommended).slice(0, 4);
        const prefill = bare(r.verdict === "ok" ? (r.requested || r.recommended) : (r.recommended || r.requested || ""));
        panel.innerHTML = head + `<div style="padding:12px 14px;">
          <div style="border-left:3px solid ${vs.color};background:rgba(148,163,184,0.06);padding:8px 10px;border-radius:3px;margin-bottom:12px;">
            <div style="font-weight:600;color:${vs.color};margin-bottom:3px;">${vs.icon} ${esc(vs.title)}</div>
            <div style="line-height:1.45;">${esc(r.message)}</div>
          </div>
          <div style="display:grid;grid-template-columns:auto 1fr;gap:3px 10px;margin-bottom:10px;">
            <span style="color:var(--muted,#94a3b8);">File CRS</span><span>${embTxt}</span>
            <span style="color:var(--muted,#94a3b8);">Project CRS</span><span style="font-family:monospace;">${esc(r.projectSrs || "not set")}</span>
            <span style="color:var(--muted,#94a3b8);">File</span><span>${esc(r.header?.software || "")} ${r.header?.version ? "· LAS " + esc(r.header.version) : ""} ${Number.isFinite(r.header?.pointCount) && r.header.pointCount ? "· " + r.header.pointCount.toLocaleString() + " pts" : ""}</span>
          </div>
          ${rows ? `<table style="width:100%;border-collapse:collapse;margin-bottom:10px;font-size:11px;">
            <thead><tr style="color:var(--muted,#94a3b8);text-align:left;"><th style="padding:3px 6px;font-weight:500;">EPSG</th><th style="padding:3px 6px;font-weight:500;">Name</th><th style="padding:3px 6px;font-weight:500;">Source</th><th style="padding:3px 6px;font-weight:500;text-align:right;">From marina</th></tr></thead>
            <tbody>${rows}</tbody></table>` : ""}
          ${alts.length ? `<div style="color:var(--muted,#94a3b8);font-size:11px;margin-bottom:10px;">Also fits (other datum realizations): ${alts.map((a) => `<a href="#" data-crs-pick="${esc(bare(a.code))}" style="color:#38bdf8;">${esc(a.code)}</a>`).join(", ")}</div>` : ""}
          <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
            <span style="color:var(--muted,#94a3b8);">Build with EPSG:</span>
            <input data-crs-epsg value="${esc(prefill)}" placeholder="project" style="${inputSty}" />
            <button data-crs-recheck style="${btn(false)}" title="Check this EPSG against the marina">Check</button>
            <span style="flex:1"></span>
            <button data-crs-cancel style="${btn(false)}">Cancel</button>
            <button data-crs-build style="${btn(true)}">${r.verdict === "no-match" ? "Build anyway" : "Build"}</button>
          </div></div>`;
      }
      panel.querySelectorAll("[data-crs-cancel]").forEach((b) => b.addEventListener("click", () => done(null)));
      panel.querySelector("[data-crs-build]")?.addEventListener("click", () => {
        const v = bare(panel.querySelector("[data-crs-epsg]")?.value?.trim());
        if (v && !/^\d{4,6}$/.test(v)) { panel.querySelector("[data-crs-epsg]")?.focus(); return; }
        app?.log?.("surface.crs.confirm", { s3Key, marina, verdict: last?.verdict || null, recommended: last?.recommended || null, chosen: v || null });
        done(v);
      });
      panel.querySelector("[data-crs-recheck]")?.addEventListener("click", () => {
        const v = bare(panel.querySelector("[data-crs-epsg]")?.value?.trim());
        if (v && !/^\d{4,6}$/.test(v)) return;
        run(v || null);
      });
      panel.querySelectorAll("[data-crs-pick]").forEach((a) => a.addEventListener("click", (e) => {
        e.preventDefault();
        const inp = panel.querySelector("[data-crs-epsg]");
        if (inp) inp.value = a.dataset.crsPick;
      }));
    };

    const run = async (epsg) => {
      paint({ loading: true });
      try {
        const qs = new URLSearchParams({ key: s3Key, marina: marina || "" });
        if (epsg) qs.set("sourceEpsg", epsg);
        const r = await fetch(`/api/project/las/crs-check?${qs}`);
        const d = await r.json().catch(() => ({}));
        if (!r.ok || !d.ok) throw new Error(d.error || `HTTP ${r.status}`);
        last = d;
        paint({ result: d });
      } catch (err) {
        paint({ error: String(err?.message || err) });
      }
    };
    run(sourceEpsg ? bare(sourceEpsg) : null);
  });
}

// ── Build polling ─────────────────────────────────────────────────────────────

function startBuildPoll(id) {
  if (buildPolls.has(id)) return;
  const poll = async () => {
    const s = catalog.find(c => c.id === id);
    if (!s || s.rasterReady) {
      buildPolls.delete(id);
      if (!buildPolls.size) surfaceTaskIdle();
      render(); return;
    }
    try {
      const r = await fetch(`/api/surfaces/build-status?surfaceId=${encodeURIComponent(s.id)}`);
      const data = await r.json();
      s.buildJob = data.job || null;
      if (data.rasterReady) {
        s.rasterReady = true;
        s.rasterPath = data.rasterPath;
        s.bands = data.bands || ["mean"];
        if (!s.bands.includes(s.settings.displayBand)) {
          s.settings.displayBand = s.bands.includes("mean") ? "mean" : s.bands[0];
        }
        buildPolls.delete(id);
        saveCatalog();
        if (visibleIds.has(id)) { hideSurface(id); showSurface(id); }
        render();
        app?.ui?.toast?.(`Surface ready: ${s.name}`);
        app?.log?.("surface.build.complete", { surfaceId: id, name: s.name, rasterPath: data.rasterPath });
        if (!buildPolls.size) surfaceTaskIdle();
        return;
      }
      if (data.job?.status === "error") {
        buildPolls.delete(id);
        render();
        app?.log?.("surface.build.error", { surfaceId: id, name: s.name, message: data.job?.message });
        if (!buildPolls.size) surfaceTaskIdle();
        return;
      }
      // Still running — update status bar with this job's progress
      surfaceTaskStatus(data.job?.message || "Building raster…", data.job?.progressPct ?? null);
    } catch {}
    buildPolls.set(id, setTimeout(poll, 3000));
    render();
  };
  buildPolls.set(id, setTimeout(poll, 1500));
}

function stopBuildPoll(id) {
  const t = buildPolls.get(id);
  if (t != null) clearTimeout(t);
  buildPolls.delete(id);
}

// Reads the built raster's real-world extent via the existing /api/qimera/cog-tiles proxy
// (a thin pass-through to TiTiler's /cog/info -- see server.js's app.use("/api/qimera/cog-tiles",
// ...)) rather than storing bounds on the catalog entry ourselves: TiTiler already computes
// this from the GeoTIFF's own georeferencing, so asking it on click is one fetch and can never
// drift out of sync with the actual file the way a cached value could.
async function zoomToSurfaceExtent(s) {
  if (!s?.rasterPath) return;
  try {
    const r = await fetch(`/api/qimera/cog-tiles/info?url=${encodeURIComponent(s.rasterPath)}`);
    const data = await r.json();
    const b = data?.bounds;
    if (!Array.isArray(b) || b.length !== 4 || !b.every(Number.isFinite)) return;
    const [west, south, east, north] = b;
    app.map.instance.fitBounds([[south, west], [north, east]], { padding: [24, 24] });
  } catch {}
}

// ── Open in 3D (proof-of-concept) ─────────────────────────────────────────────
// Links a surface to its own tokenized point-cloud sandbox view of the same source file it
// was rasterized from (POST /api/surfaces/:surfaceId/open-3d in server.js kicks off a LAS->COPC
// build the first time, then mints a sandbox token) -- same self-scheduling-poll shape as
// startBuildPoll above, since the backend job is async. Auto-opens the sandbox in a new tab
// the moment it's ready; a later click on an already-ready surface just reopens it instantly,
// since the endpoint itself short-circuits once a token exists.
function pollOpen3d(id) {
  if (open3dPolls.has(id)) return;
  const step = async () => {
    const s = catalog.find(c => c.id === id);
    if (!s) { open3dPolls.delete(id); return; }
    try {
      const r = await fetch(`/api/surfaces/${encodeURIComponent(id)}/open-3d`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ marina: activeMarinaCode }),
      });
      const data = await r.json();
      if (data.ok && data.status === "ready" && data.url) {
        s.open3dStatus = "ready"; s.open3dUrl = data.url;
        open3dPolls.delete(id);
        // Opened from a timer, not a click, so popup blockers often refuse it -- then the
        // button itself says the view is ready instead of silently doing nothing.
        const win = window.open(data.url, "_blank");
        s.open3dBlocked = !win;
        render();
        return;
      }
      if (!data.ok || data.status === "failed" || data.error) {
        s.open3dStatus = "failed"; s.open3dError = data.error || null;
        open3dPolls.delete(id); render();
        return;
      }
      s.open3dStatus = data.status || "queued";
      s.open3dElapsed = data.elapsedSec ?? null;
      s.open3dBytes = data.sourceBytes ?? null;
      s.open3dRestarted = !!data.restarted;
    } catch {
      s.open3dStatus = "failed";
      open3dPolls.delete(id); render();
      return;
    }
    open3dPolls.set(id, setTimeout(step, 3000));
    render();
  };
  open3dPolls.set(id, setTimeout(step, 300));
}

// ── GeoTIFF export (mosaic2d → DAM) ──────────────────────────────────────────
// Data export: the server merges the mosaic's per-tile COGs into one GeoTIFF of
// real values (float32 elevation / uint8 classes) — the on-screen styling is not
// baked in. Optional clip to a polygon drawn on the map; result lands in the DAM
// under geotiff-files/.

let exportPolygon = null;     // committed area: [[lat,lng], ...] (Leaflet order)
let exportDrawing = false;
let exportDrawPoints = [];
let exportDrawLayer = null;   // dashed polyline preview while drawing
let exportAreaLayer = null;   // committed polygon shown on the map
let exportJob = null;         // { id, status, progressPct, message }
let exportPollTimer = null;
let exportApplyMask = true;   // bake the surface's active dock mask (if any) into the export

const EXPORT_AREA_STYLE = { color: "#38bdf8", weight: 2, dashArray: "6,4", fillColor: "#38bdf8", fillOpacity: 0.08 };

// Setting the container's own inline cursor isn't enough — Leaflet's default stylesheet
// puts its own `cursor` rule on `.leaflet-interactive` elements (markers, other tools'
// overlays), which wins over the container's inline style for hover. Inject an
// `!important` rule instead (same fix the Cross Sections tool already uses).
let surfCursorStyleEl = null;
function setDrawCursor(on) {
  if (on && !surfCursorStyleEl) {
    surfCursorStyleEl = document.createElement("style");
    surfCursorStyleEl.textContent = `.leaflet-container,.leaflet-interactive{cursor:crosshair!important}`;
    document.head.appendChild(surfCursorStyleEl);
  } else if (!on && surfCursorStyleEl) {
    surfCursorStyleEl.remove();
    surfCursorStyleEl = null;
  }
}

function startExportDraw() {
  const map = app?.map?.instance;
  if (!map || exportDrawing) return;
  exportDrawing = true;
  exportDrawPoints = [];
  setDrawCursor(true);
  map.doubleClickZoom?.disable?.();
  map.on("click", onExportMapClick);
  map.on("dblclick", onExportMapDblClick);
  render();
}

function stopExportDraw() {
  const map = app?.map?.instance;
  exportDrawing = false;
  exportDrawPoints = [];
  setDrawCursor(false);
  if (map) {
    map.doubleClickZoom?.enable?.();
    map.off("click", onExportMapClick);
    map.off("dblclick", onExportMapDblClick);
  }
  if (exportDrawLayer) { try { exportDrawLayer.remove(); } catch {} exportDrawLayer = null; }
  render();
}

function onExportMapClick(e) {
  const map = app?.map?.instance;
  exportDrawPoints.push([e.latlng.lat, e.latlng.lng]);
  if (!exportDrawLayer) {
    exportDrawLayer = L.polyline(exportDrawPoints, EXPORT_AREA_STYLE).addTo(map);
  } else {
    exportDrawLayer.setLatLngs(exportDrawPoints);
  }
  render(); // keep the vertex counter in the panel live
}

function onExportMapDblClick(e) {
  L.DomEvent.stop(e); // prevent map zoom
  // The dblclick also fired a click that already added the final point — remove it
  if (exportDrawPoints.length > 0) exportDrawPoints.pop();
  if (exportDrawPoints.length >= 3) commitExportPolygon(exportDrawPoints.slice());
  stopExportDraw();
}

function commitExportPolygon(points) {
  clearExportPolygon();
  exportPolygon = points;
  const map = app?.map?.instance;
  if (map) exportAreaLayer = L.polygon(points, EXPORT_AREA_STYLE).addTo(map);
}

function clearExportPolygon() {
  exportPolygon = null;
  if (exportAreaLayer) { try { exportAreaLayer.remove(); } catch {} exportAreaLayer = null; }
}

function resetExportState() {
  stopExportDraw();
  clearExportPolygon();
  if (exportPollTimer) { clearTimeout(exportPollTimer); exportPollTimer = null; }
  exportJob = null;
}

async function startExport(s) {
  if (!activeMarinaCode || exportJob?.status === "queued" || exportJob?.status === "running") return;
  const isMosaicExport = s?.kind === "mosaic2d";
  const attribute = s?.attr || "elevation";
  const maskRange = exportApplyMask ? activeMaskRange(s) : null;
  const masked = !!maskRange;
  const polygon = exportPolygon ? exportPolygon.map(([lat, lng]) => [lng, lat]) : null; // server expects GeoJSON-order [lon,lat] WGS84
  // Bakes the Dock Centerline mask in as real nodata (not just render-time transparency) --
  // server clamps/validates, so it's safe to send whatever the surface currently has dialed in.
  const maskParams = masked ? { maskMin: maskRange.min, maskMax: maskRange.max } : {};
  // Mosaic surfaces have no single file to point at -- the mosaic2d route collects tiles
  // itself. A built single-COG surface already is one file, so it goes through the matching
  // single-raster export route instead (same mask/warp/upload job, just one input file).
  const url = isMosaicExport
    ? `/api/point-cloud/mosaic2d/${encodeURIComponent(activeMarinaCode)}/export`
    : "/api/qimera/cog-export-dam";
  // Surface's own display name -- lets the DAM file keep the name the user gave this
  // surface instead of falling back to the generate-deliverables naming convention
  // (standardDeliverableBaseName), which collapses distinct surfaces/regions to the same name.
  const name = s?.name || "";
  const body = isMosaicExport
    ? { attribute, polygon, name, ...maskParams }
    : { rasterPath: s.rasterPath, marina: activeMarinaCode, polygon, name, ...maskParams };
  try {
    const r = await fetch(url, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    const d = await r.json();
    if (!r.ok || !d?.ok) throw new Error(d?.error || `HTTP ${r.status}`);
    exportJob = { id: d.jobId, status: "queued", progressPct: 0, message: "Queued..." };
    app?.log?.(isMosaicExport ? "surface.mosaic2d.export.start" : "surface.qimera.export.start", { marina: activeMarinaCode, attribute, clipped: !!exportPolygon, masked, jobId: d.jobId });
    pollExportJob();
    render();
  } catch (err) {
    app?.ui?.toast?.(`Export failed to start: ${String(err?.message || err)}`);
  }
}

function pollExportJob() {
  if (exportPollTimer) clearTimeout(exportPollTimer);
  exportPollTimer = setTimeout(async () => {
    if (!exportJob) return;
    try {
      const r = await fetch(`/api/point-cloud/generate-tiles/jobs/${encodeURIComponent(exportJob.id)}`);
      const d = await r.json();
      if (d?.job) {
        exportJob = { id: exportJob.id, ...d.job };
        if (d.job.status === "completed") {
          surfaceTaskIdle();
          app?.ui?.toast?.(d.job.message || "GeoTIFF export ready in the DAM.");
          app?.log?.("surface.mosaic2d.export.complete", { jobId: exportJob.id, damKey: d.job.damKey });
          // The DAM (image-generator tool) renders its category file lists from its own
          // cached state, which it has no other way to know just went stale -- same
          // cross-tool refresh signal Generate Deliverables' registerDeliverableEntry uses,
          // so the new file shows up in geotiff-files without a manual project refresh.
          if (activeMarinaCode) {
            document.dispatchEvent(new CustomEvent("hydrotools:deliverable-registered", { detail: { code: activeMarinaCode, category: "geotiff-files" } }));
          }
          render();
          return;
        }
        if (d.job.status === "failed") {
          surfaceTaskIdle();
          app?.ui?.toast?.(`GeoTIFF export failed: ${d.job.error || d.job.message || "unknown error"}`);
          render();
          return;
        }
        surfaceTaskStatus(d.job.message || "Exporting GeoTIFF…", d.job.progressPct ?? null);
        render();
      }
    } catch {}
    pollExportJob();
  }, 2500);
}

// ── Dock centerline tool ─────────────────────────────────────────────────────
// Port of the Potree "Draw Dock Centerline" tool (point-cloud-workspace's
// computeTopOfDeck / dockLineSamples) onto the floating-point surface, so masking a
// dock no longer requires the Potree clip step at all. Potree samples raw points in a
// corridor around a drawn line and bins their Z; we have no point cloud here, so the
// corridor (buffered with turf, in feet) is sent to the server, which asks TiTiler for
// a real per-pixel histogram of the raster within that corridor — same downstream
// "find the densest elevation cluster" logic either way.
let dockLineActive = false;
let dockLineStartLatLng = null;
let dockLinePreviewLayer = null;
let dockLineBusy = false;
let dockLineError = "";
let dockLineWidth = 2;       // ft — corridor width across the drawn line
let dockLineTolerance = 0.5; // ft — ± padding around the detected deck elevation
const dockLineSamplesByS = new Map(); // surfaceId -> [{id, topOfDeck, count}]

function getDockLineSamples(id) {
  if (!dockLineSamplesByS.has(id)) dockLineSamplesByS.set(id, []);
  return dockLineSamplesByS.get(id);
}

// Same peak-bin-cluster approach as point-cloud-workspace's computeTopOfDeck, adapted
// to run on a pre-binned histogram (TiTiler's zonal statistics) instead of raw point
// elevations: find the densest bin, grow a contiguous cluster around it while neighbor
// bins stay above 25% of the peak count, then take the count-weighted mean of that
// cluster — robust to a minority of pixels sitting above (railings, boats) or below
// (water glare, gaps) the actual deck surface.
function computeTopOfDeckFromHistogram(counts, edges) {
  if (!counts?.length) return null;
  let peakIdx = 0;
  for (let i = 1; i < counts.length; i++) if (counts[i] > counts[peakIdx]) peakIdx = i;
  if (counts[peakIdx] <= 0) return null;
  const threshold = counts[peakIdx] * 0.25;
  let startIdx = peakIdx;
  while (startIdx > 0 && counts[startIdx - 1] >= threshold) startIdx--;
  let endIdx = peakIdx;
  while (endIdx < counts.length - 1 && counts[endIdx + 1] >= threshold) endIdx++;
  let sum = 0, n = 0;
  for (let i = startIdx; i <= endIdx; i++) {
    const mid = ((edges[i] ?? 0) + (edges[i + 1] ?? 0)) / 2;
    sum += mid * counts[i];
    n += counts[i];
  }
  return n > 0 ? sum / n : ((edges[peakIdx] + edges[peakIdx + 1]) / 2);
}

function startDockLineDraw() {
  const map = app?.map?.instance;
  if (!map || dockLineActive) return;
  dockLineActive = true;
  dockLineStartLatLng = null;
  dockLineError = "";
  setDrawCursor(true);
  map.on("click", onDockLineMapClick);
  render();
}

function stopDockLineDraw() {
  const map = app?.map?.instance;
  dockLineActive = false;
  dockLineStartLatLng = null;
  setDrawCursor(false);
  if (map) {
    map.off("click", onDockLineMapClick);
    map.off("mousemove", onDockLineMouseMove);
  }
  if (dockLinePreviewLayer) { try { dockLinePreviewLayer.remove(); } catch {} dockLinePreviewLayer = null; }
  render();
}

function onDockLineMapClick(e) {
  const map = app?.map?.instance;
  if (!dockLineStartLatLng) {
    dockLineStartLatLng = e.latlng;
    if (dockLinePreviewLayer) { try { dockLinePreviewLayer.remove(); } catch {} }
    dockLinePreviewLayer = L.polyline([e.latlng, e.latlng], { color: "#f59e0b", weight: 3 }).addTo(map);
    map.on("mousemove", onDockLineMouseMove);
    return;
  }
  const p1 = dockLineStartLatLng;
  const p2 = e.latlng;
  map.off("mousemove", onDockLineMouseMove);
  const s = getSelected();
  stopDockLineDraw();
  if (s) runDockLineQuery(s, p1, p2);
}

function onDockLineMouseMove(e) {
  if (dockLinePreviewLayer && dockLineStartLatLng) dockLinePreviewLayer.setLatLngs([dockLineStartLatLng, e.latlng]);
}

async function runDockLineQuery(s, p1, p2) {
  if (!s || (!s.rasterPath && s.kind !== "mosaic2d")) return;
  dockLineBusy = true; dockLineError = ""; render();
  try {
    const line = turf.lineString([[p1.lng, p1.lat], [p2.lng, p2.lat]]);
    const widthFt = Math.max(0.2, Number(dockLineWidth) || 2);
    const corridor = turf.buffer(line, widthFt / 2, { units: "feet" });
    const ring = corridor?.geometry?.coordinates?.[0];
    if (!ring || ring.length < 3) throw new Error("Could not build a corridor from that line.");
    // Single Qimera COGs query TiTiler directly against that one file; the mosaic has no
    // single file, so its variant collects just the tiles the corridor touches server-side
    // (see /dockline-stats) and queries that instead — same response shape either way.
    const url = s.kind === "mosaic2d"
      ? `/api/point-cloud/mosaic2d/${encodeURIComponent(s.marinaCode || activeMarinaCode)}/dockline-stats`
      : "/api/surfaces/dockline-stats";
    const body = s.kind === "mosaic2d"
      ? { attribute: s.attr || "elevation", polygon: ring }
      : { rasterPath: s.rasterPath, polygon: ring };
    const r = await fetch(url, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const d = await r.json();
    if (!r.ok || !d?.ok) throw new Error(d?.error || `HTTP ${r.status}`);
    const [counts, edges] = d.statistics?.histogram || [[], []];
    const total = counts.reduce((a, b) => a + b, 0);
    if (!total) throw new Error("No elevation data found along that line — try a wider corridor or a different spot.");
    const topOfDeck = computeTopOfDeckFromHistogram(counts, edges);
    if (!Number.isFinite(topOfDeck)) throw new Error("Could not determine a deck elevation from that line.");
    getDockLineSamples(s.id).push({ id: uid(), topOfDeck, count: Math.round(total) });
    applyDockLineUnion(s);
    app?.log?.("surface.dockline.sample", { surfaceId: s.id, topOfDeck: Math.round(topOfDeck * 100) / 100, count: Math.round(total) });
  } catch (err) {
    dockLineError = String(err?.message || err);
  } finally {
    dockLineBusy = false; render();
  }
}

// Recomputes the mask band as the union of every sample's topOfDeck ± tolerance — min of all
// the lower edges, max of all the upper edges — same as Potree's recomputeDockLineUnionClip.
// Writes into whichever representation the surface's current colormap actually shows on
// screen, so the tool nudges bars the user can see and hand-tweak afterward, not a hidden
// value: the Custom (colormapName "solid") bins editor's dividers/per-bin transparency for a
// solid-color surface, or the interval ramp's rescale range + underflow/overflow transparency
// otherwise. Either way, activeMaskRange() below reads back exactly what this wrote.
function applyDockLineUnion(s) {
  const samples = getDockLineSamples(s.id);
  if (!samples.length) return;
  const tol = Math.max(0, Number(dockLineTolerance) || 0.5);
  let lo = Infinity, hi = -Infinity;
  for (const sm of samples) {
    lo = Math.min(lo, sm.topOfDeck - tol);
    hi = Math.max(hi, sm.topOfDeck + tol);
  }
  lo = Math.round(lo * 100) / 100;
  hi = Math.round(hi * 100) / 100;
  if (s.settings.colormapName === "solid") {
    s.settings.customBins = 3;
    const defaults = [[220,50,50],[56,189,248],[34,197,94]];
    s.settings.customColors = [defaults[0], s.settings.customColors?.[1] || [56,189,248], defaults[2]];
    s.settings.customTransparent = [true, false, true];
    s.settings.customDividers = [lo, hi];
  } else {
    s.settings.rescaleMin = lo;
    s.settings.rescaleMax = hi;
    s.settings.underflowTransparent = true;
    s.settings.overflowTransparent = true;
  }
  const rt = surfaceRuntimes.get(s.id);
  if (rt) { rt.histViewRange = null; rt.histViewData = null; }
  saveCatalog();
  if (visibleIds.has(s.id)) refreshSurface(s.id);
  render();
}

// The real, currently-visible mask boundary for a surface — {min, max} in the raster's native
// units, or null if nothing's actively masked. Reads whichever representation applyDockLineUnion
// (or the matching manual controls) actually writes to, so hand-editing the bars after drawing
// a dock line changes what exports, exactly like it changes what renders on screen.
function activeMaskRange(s) {
  const st = s?.settings;
  if (!st) return null;
  if (st.colormapName === "solid") {
    const bins = Math.max(1, Number(st.customBins) || 0);
    const divs = Array.isArray(st.customDividers) ? [...st.customDividers].sort((a, b) => a - b) : [];
    if (divs.length !== bins - 1) return null;
    const transparent = st.customTransparent || [];
    const opaqueIdx = [];
    for (let i = 0; i < bins; i++) if (!transparent[i]) opaqueIdx.push(i);
    if (!opaqueIdx.length) return null;
    const first = opaqueIdx[0], last = opaqueIdx[opaqueIdx.length - 1];
    // Only a single contiguous opaque band with a real (non-infinite) edge on both sides
    // reads as "a mask" -- everything opaque, or an opaque region open at one end, has no
    // finite boundary to export as nodata on that side.
    if (last - first !== opaqueIdx.length - 1) return null;
    if (first === 0 || last === bins - 1) return null;
    return { min: divs[first - 1], max: divs[last] };
  }
  if (st.underflowTransparent && st.overflowTransparent && Number.isFinite(st.rescaleMin) && Number.isFinite(st.rescaleMax)) {
    return { min: st.rescaleMin, max: st.rescaleMax };
  }
  return null;
}

function hasActiveMask(s) {
  return !!activeMaskRange(s);
}

function removeDockLineSample(s, sampleId) {
  const samples = getDockLineSamples(s.id).filter((sm) => sm.id !== sampleId);
  dockLineSamplesByS.set(s.id, samples);
  if (samples.length) applyDockLineUnion(s);
  else render();
}

function clearDockLineSamples(s) {
  dockLineSamplesByS.set(s.id, []);
  render();
}

// ── Map layer management ──────────────────────────────────────────────────────

function getSurfaceRuntime(id) {
  if (!surfaceRuntimes.has(id)) {
    const paneName = `surfPane_${id}`;
    const hillPaneName = `surfHillPane_${id}`;
    const map = app.map.instance;
    // leaflet-rotate splits _mapPane into a rotated child (_rotatePane, home to the
    // default tilePane/overlayPane) and an unrotated one (_norotatePane, for markers/
    // labels that should stay upright). map.createPane() with no explicit container
    // parents new panes under the *unrotated* _mapPane itself, so without passing
    // _rotatePane here, this surface's tiles just sat still while the basemap rotated
    // around them.
    const rotatedParent = map._rotatePane || undefined;
    let paneEl, hillPaneEl;
    try { paneEl = map.createPane(paneName, rotatedParent); } catch { paneEl = map.getPane(paneName); }
    try { hillPaneEl = map.createPane(hillPaneName, rotatedParent); } catch { hillPaneEl = map.getPane(hillPaneName); }
    // Between the basemap (tilePane, z=200) and Leaflet's default vector pane (overlayPane,
    // z=400, where slip polygons/dock lines/boundaries/most other "linework" render unless a
    // tool sets its own pane) -- a surface should sit above the basemap but never cover
    // linework. Previously based at 810, which put every surface above overlayPane (400),
    // markerPane (600), and even popupPane (700) -- linework was rendering *under* the raster.
    // Capped so even a large stack of surfaces in one marina can't creep up into overlayPane's
    // range.
    const zBase = 210 + Math.min(90, catalog.length - catalog.findIndex(s => s.id === id)) * 2;
    paneEl.style.zIndex = String(zBase);
    hillPaneEl.style.zIndex = String(zBase + 1);
    hillPaneEl.style.mixBlendMode = "multiply";
    surfaceRuntimes.set(id, { tileLayer: null, hillshadeLayer: null, colormapId: null, showPending: false, paneEl, hillPaneEl, paneName, hillPaneName, surfId: id, histData: null, histFetching: false, histViewRange: null, histViewData: null, histViewFetching: false, histViewFetchTimer: null, tileProgressState: { pending: 0, total: 0, timer: null }, flatRegionsLayer: null, flatRegionsOpen: false, flatRegionsBusy: false, flatRegionsCount: null, flatRegionsError: null, flatRegionsParams: null });
  }
  return surfaceRuntimes.get(id);
}

function ensureCustomDividers(s, lo, hi) {
  const st = s.settings;
  const defaults = [[220,50,50],[56,189,248],[34,197,94],[251,191,36],[167,139,250]];
  while (st.customColors.length < st.customBins) st.customColors.push(defaults[st.customColors.length % defaults.length]);
  while (st.customTransparent.length < st.customBins) st.customTransparent.push(false);
  if (!st.customDividers || st.customDividers.length !== st.customBins - 1) {
    st.customDividers = Array.from({length: st.customBins - 1}, (_, i) => lo + (hi - lo) * (i + 1) / st.customBins);
  }
  return [...st.customDividers].sort((a, b) => a - b);
}

function buildIntervalColormap(s) {
  const { colormapName, underflowColor, overflowColor, underflowTransparent, overflowTransparent } = s.settings;
  if (colormapName === "solid") {
    const rt = surfaceRuntimes.get(s.id);
    const hd = rt?.histData || null;
    const lo = hd?.[1]?.[0] ?? (s.settings.rescaleMin ?? -50);
    const hi = hd?.[1]?.[hd[1].length-1] ?? (s.settings.rescaleMax ?? 0);
    const divs = ensureCustomDividers(s, lo, hi);
    const far = (Math.abs(hi - lo) + 1) * 1000;
    const a = (i) => (s.settings.customTransparent[i] ? 0 : 255);
    const col = (i) => [...(s.settings.customColors[Math.min(i, s.settings.customColors.length-1)] || [128,128,128]), a(i)];
    if (divs.length === 0) return JSON.stringify([[[-far, far], col(0)]]);
    const entries = [];
    entries.push([[-far, divs[0]], col(0)]);
    for (let i = 0; i < divs.length - 1; i++) entries.push([[divs[i], divs[i+1]], col(i+1)]);
    entries.push([[divs[divs.length-1], far], col(divs.length)]);
    return JSON.stringify(entries);
  }
  const rMin = s.settings.rescaleMin ?? -50;
  const rMax = s.settings.rescaleMax ?? 0;
  const span = rMax - rMin;
  if (Math.abs(span) < 0.001) {
    const [r,g,b] = getColormapColor(0.5, colormapName);
    return JSON.stringify([[[rMin-1, rMax+1], [r,g,b,255]]]);
  }
  const pad = (Math.abs(span)+1)*100;
  const steps = 256;
  const entries = [];
  entries.push([[rMin-pad, rMin], [...underflowColor, underflowTransparent ? 0 : 255]]);
  for (let i = 0; i < steps; i++) {
    const t0 = i/steps, t1 = (i+1)/steps;
    const [r,g,b] = getColormapColor((t0+t1)/2, colormapName);
    entries.push([[rMin+t0*span, rMin+t1*span], [r,g,b,255]]);
  }
  entries.push([[rMax, rMax+pad], [...overflowColor, overflowTransparent ? 0 : 255]]);
  return JSON.stringify(entries);
}

// 1-based GDAL/TiTiler band index for a multi-band surface's currently-selected grid
// statistic, or null for a single-band (or not-yet-built) surface -- callers append
// &bidx=<n> only when this is non-null, leaving single-band surfaces/mosaic2d untouched.
function surfaceBandIndex(s) {
  if (!Array.isArray(s.bands) || s.bands.length < 2) return null;
  const idx = s.bands.indexOf(s.settings?.displayBand);
  return (idx >= 0 ? idx : 0) + 1;
}

// Both raster kinds speak the same TiTiler param contract — only the base route differs:
// single Qimera COGs go through /api/qimera/cog-tiles (url= points at the local COG), the
// project-wide 2D mosaic through its own endpoint (the server resolves the MosaicJSON doc).
function tileUrlBase(s) {
  const base = s.kind === "mosaic2d"
    ? `/api/point-cloud/mosaic2d/${encodeURIComponent(s.marinaCode || "")}/tiles/{z}/{x}/{y}.png?v=${encodeURIComponent(mosaicAvailable?.updatedAt || "")}${s.attr ? `&attr=${encodeURIComponent(s.attr)}` : ""}`
    : `/api/qimera/cog-tiles/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodeURIComponent(s.rasterPath)}`;
  const bidx = surfaceBandIndex(s);
  return bidx ? `${base}&bidx=${bidx}` : base;
}

function buildTileUrl(s, cmId = null) {
  if (isClsMosaic(s)) {
    // Tiny discrete colormap — passed inline, no registry round-trip. The server forces
    // nearest resampling for categorical data regardless, but say what we mean.
    return `${tileUrlBase(s)}&colormap=${encodeURIComponent(buildClassificationColormapJson(s))}&resampling=nearest`;
  }
  const cmParam = cmId
    ? `colormap_id=${encodeURIComponent(cmId)}`
    : `colormap=${encodeURIComponent(buildIntervalColormap(s))}`;
  return `${tileUrlBase(s)}&${cmParam}&resampling=${s.settings.tileResampling||"bilinear"}`;
}

function buildHillshadeUrl(s) {
  const params = encodeURIComponent(JSON.stringify({ azimuth: s.settings.hillshadeAzimuth, angle_altitude: s.settings.hillshadeAltitude, z_factor: s.settings.tileZFactor ?? 1.0 }));
  return `${tileUrlBase(s)}&algorithm=hillshade&algorithm_params=${params}`;
}

// Canvas-composite hillshade layer: uses the colormap tile's alpha channel as a mask so
// underflow/overflow transparent regions are also transparent in the hillshade layer.
function buildMaskedHillshadeLayer(hillshadeUrlTmpl, colormapUrlTmpl, options) {
  const layer = L.gridLayer(options);
  layer.createTile = function(coords, done) {
    const size = this.getTileSize();
    const canvas = document.createElement("canvas");
    canvas.width = size.x; canvas.height = size.y;
    const ctx = canvas.getContext("2d");
    const hUrl = L.Util.template(hillshadeUrlTmpl, coords);
    const cUrl = L.Util.template(colormapUrlTmpl, coords);
    let loaded = 0;
    const hs = new Image(), cm = new Image();
    const onLoad = () => {
      if (++loaded < 2) return;
      ctx.drawImage(hs, 0, 0);
      ctx.globalCompositeOperation = "destination-in";
      ctx.drawImage(cm, 0, 0);
      done(null, canvas);
    };
    const onErr = (e) => done(e, canvas);
    hs.crossOrigin = cm.crossOrigin = "anonymous";
    hs.onload = cm.onload = onLoad;
    hs.onerror = cm.onerror = onErr;
    hs.src = hUrl; cm.src = cUrl;
    return canvas;
  };
  return layer;
}

// Create a hillshade layer — masked when underflow/overflow transparency is active.
function createHillshadeLayer(s, rt, map, cmId = null) {
  const opts = { tileSize: 256, opacity: s.settings.tileOpacity, maxZoom: 24, pane: rt.hillPaneName };
  const needsMask = s.settings.underflowTransparent || s.settings.overflowTransparent;
  if (needsMask && s.settings.rescaleMin != null && s.settings.rescaleMax != null) {
    return buildMaskedHillshadeLayer(buildHillshadeUrl(s), buildTileUrl(s, cmId), opts).addTo(map);
  }
  return L.tileLayer(buildHillshadeUrl(s), opts).addTo(map);
}

function buildPaneFilter(s) {
  const parts = [];
  const gamma = s.settings.tileGamma ?? 1.0;
  const sat   = s.settings.tileSaturation ?? 1.0;
  const con   = s.settings.tileContrast ?? 1.0;
  if (gamma !== 1.0) parts.push(`brightness(${gamma.toFixed(2)})`);
  if (sat   !== 1.0) parts.push(`saturate(${sat.toFixed(2)})`);
  if (con   !== 1.0) parts.push(`contrast(${con.toFixed(2)})`);
  return parts.join(" ");
}

function bindTileProgress(rt, layer) {
  if (!layer) return;
  layer.on("loading", () => {
    if (rt.tileProgressState.timer) clearTimeout(rt.tileProgressState.timer);
    rt.tileProgressState = { pending:0, total:0, timer:null };
    updateProgressUI(rt);
  });
  layer.on("tileloadstart", () => { rt.tileProgressState.total++; rt.tileProgressState.pending++; updateProgressUI(rt); });
  layer.on("tileload tileerror", () => {
    rt.tileProgressState.pending = Math.max(0, rt.tileProgressState.pending-1);
    updateProgressUI(rt);
    if (rt.tileProgressState.pending <= 0 && rt.tileProgressState.total > 0) {
      rt.tileProgressState.timer = setTimeout(() => { rt.tileProgressState = { pending:0, total:0, timer:null }; updateProgressUI(rt); }, 800);
    }
  });
}

function updateProgressUI(rt) {
  if (!container) return;
  const { pending, total } = rt.tileProgressState;
  const bar = container.querySelector(`[data-surf-tile-bar="${rt.surfId}"]`);
  const wrap = container.querySelector(`[data-surf-tile-wrap="${rt.surfId}"]`);
  if (!wrap) return;
  if (!total) { wrap.style.display = "none"; return; }
  const loaded = total - pending;
  const pct = total > 0 ? Math.round((loaded/total)*100) : 0;
  wrap.style.display = "block";
  if (bar) bar.style.width = `${pct}%`;
}

async function showSurface(id) {
  const s = catalog.find(c => c.id === id);
  if (!s || !s.rasterReady || (!s.rasterPath && s.kind !== "mosaic2d")) return;
  const rt = getSurfaceRuntime(id);
  if (rt.showPending) return;
  rt.showPending = true;
  try {
    const map = app.map.instance;
    if (!rt.tileLayer) {
      let cmId = null;
      if (!isClsMosaic(s)) {
        cmId = await registerColormap(buildIntervalColormap(s));
        rt.colormapId = cmId;
      }
      rt.tileLayer = L.tileLayer(buildTileUrl(s, cmId), { tileSize:256, opacity:s.settings.tileOpacity, maxZoom:24, pane:rt.paneName }).addTo(map);
      bindTileProgress(rt, rt.tileLayer);
    }
    // Hillshade is a slope derivative — meaningless over categorical class codes.
    if (s.settings.hillshadeOverlay && !rt.hillshadeLayer && !isClsMosaic(s)) {
      rt.hillshadeLayer = createHillshadeLayer(s, rt, map, rt.colormapId ?? null);
      bindTileProgress(rt, rt.hillshadeLayer);
    }
    rt.paneEl.style.filter = buildPaneFilter(s);
    visibleIds.add(id);
    if (!rt.histData && !rt.histFetching && !isClsMosaic(s)) fetchHistogram(id);
  } finally {
    rt.showPending = false;
  }
}

function hideSurface(id) {
  const rt = surfaceRuntimes.get(id);
  if (!rt) return;
  if (rt.tileLayer) { try { rt.tileLayer.remove(); } catch {} rt.tileLayer = null; }
  if (rt.hillshadeLayer) { try { rt.hillshadeLayer.remove(); } catch {} rt.hillshadeLayer = null; }
  if (rt.flatRegionsLayer) { try { rt.flatRegionsLayer.remove(); } catch {} rt.flatRegionsLayer = null; }
  rt.colormapId = null;
  visibleIds.delete(id);
}

async function refreshSurface(id) {
  if (!visibleIds.has(id)) return;
  hideSurface(id);
  await showSurface(id);
}

async function fetchHistogram(id) {
  const s = catalog.find(c => c.id === id);
  if (!s || (!s.rasterPath && s.kind !== "mosaic2d") || isClsMosaic(s)) return;
  const rt = getSurfaceRuntime(id);
  if (rt.histFetching) return;
  rt.histFetching = true;
  try {
    if (s.kind === "mosaic2d") {
      // No whole-mosaic statistics endpoint (it would open every COG) — synthesize a
      // histogram from the per-tile min/max already stored in the mosaic manifest by
      // spreading each tile's weight uniformly across its range. Coarse, but plenty for
      // the rescale slider and the colored histogram bars.
      const r = await fetch(`/api/point-cloud/mosaic2d/${encodeURIComponent(s.marinaCode || "")}`);
      if (!r.ok) return;
      const d = await r.json();
      const attrKey = s.attr || "elevation";
      const ranges = Object.values(d?.tiles || {})
        .map(t => (attrKey === "elevation" ? t : t.attrs?.[attrKey]) || {})
        .map(t => [Number(t.min), Number(t.max)])
        .filter(([lo, hi]) => Number.isFinite(lo) && Number.isFinite(hi) && hi >= lo);
      if (!ranges.length) return;
      const gLo = Math.min(...ranges.map(r2 => r2[0]));
      const gHi = Math.max(...ranges.map(r2 => r2[1]));
      const N = 256;
      const step = (gHi - gLo) / N || 1;
      const edges = Array.from({ length: N + 1 }, (_, i) => gLo + i * step);
      const counts = new Array(N).fill(0);
      for (const [lo, hi] of ranges) {
        const first = Math.max(0, Math.floor((lo - gLo) / step));
        const last = Math.min(N - 1, Math.ceil((hi - gLo) / step) - 1);
        for (let j = first; j <= Math.max(first, last); j++) counts[j] += 1;
      }
      rt.histData = [counts, edges];
      if (s.settings.rescaleMin === null) s.settings.rescaleMin = Math.round(gLo * 10) / 10;
      if (s.settings.rescaleMax === null) s.settings.rescaleMax = Math.round(gHi * 10) / 10;
      refreshSurface(id);
      if (id === selectedId) render();
      return;
    }
    const bidxParam = surfaceBandIndex(s) ? `&bidx=${surfaceBandIndex(s)}` : "";
    const r = await fetch(`/api/qimera/cog-tiles/statistics?url=${encodeURIComponent(s.rasterPath)}&histogram_bins=256${bidxParam}`);
    if (!r.ok) return;
    const data = await r.json();
    const band = data.b1 || Object.values(data)[0];
    if (band?.histogram) {
      rt.histData = band.histogram;
      if (s.settings.rescaleMin === null) s.settings.rescaleMin = Math.round((band.percentile_2 ?? band.min ?? -50)*10)/10;
      if (s.settings.rescaleMax === null) s.settings.rescaleMax = Math.round((band.percentile_98 ?? band.max ?? 0)*10)/10;
      refreshSurface(id);
      if (id === selectedId) render();
    }
  } catch {} finally {
    rt.histFetching = false;
  }
}

// Fetches a finer histogram scoped to [lo, hi] (via TiTiler's histogram_range) so the
// rescale sliders get real resolution when narrowed onto a tight band like a dock deck —
// the whole-raster histogram above only has 256 bins spread across the entire elevation
// range, which can put a 0.5ft dock band inside a single bin. Mosaic2d surfaces have no
// real per-pixel histogram endpoint (their histogram is synthesized from tile min/max),
// so zooming wouldn't reveal anything real — scoped to single-COG surfaces only.
async function fetchHistogramView(id, lo, hi) {
  const s = catalog.find(c => c.id === id);
  const rt = surfaceRuntimes.get(id);
  if (!s || !rt || !s.rasterPath || isClsMosaic(s)) return;
  rt.histViewFetching = true;
  render();
  try {
    const bidxParam = surfaceBandIndex(s) ? `&bidx=${surfaceBandIndex(s)}` : "";
    const r = await fetch(`/api/qimera/cog-tiles/statistics?url=${encodeURIComponent(s.rasterPath)}&histogram_bins=256&histogram_range=${lo},${hi}${bidxParam}`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const data = await r.json();
    const band = data.b1 || Object.values(data)[0];
    rt.histViewData = band?.histogram || null;
  } catch {
    rt.histViewData = null;
  } finally {
    rt.histViewFetching = false;
    render();
  }
}

// "Find Flat Regions" first-pass dock-candidate detector -- thresholds the surface's Std Dev
// band server-side (POST /api/surfaces/isolate-flat) and renders the result as a plain
// L.geoJSON overlay (default overlayPane, z=400 -- already above every surface's own tile
// pane, see getSurfaceRuntime's zBase comment, so the highlight sits visibly on top).
async function runIsolateFlat(id) {
  const s = catalog.find(c => c.id === id);
  const rt = getSurfaceRuntime(id);
  if (!s || !s.rasterPath || !s.bands?.includes("stdev")) return;
  const stdevBandIdx = s.bands.indexOf("stdev") + 1;
  const p = rt.flatRegionsParams || { stdevMax: 0.5, minAreaSqFt: 200, minElongation: 2 };
  rt.flatRegionsBusy = true;
  rt.flatRegionsError = null;
  render();
  try {
    const res = await fetch("/api/surfaces/isolate-flat", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        rasterPath: s.rasterPath, stdevBandIdx,
        resolution: s.resolution, resolutionUnit: s.settings.displayUnit || "ft",
        stdevMax: p.stdevMax, minAreaSqFt: p.minAreaSqFt, minElongation: p.minElongation,
      }),
    });
    const data = await res.json();
    if (!res.ok || !data?.ok) throw new Error(data?.error || `HTTP ${res.status}`);
    if (rt.flatRegionsLayer) { try { rt.flatRegionsLayer.remove(); } catch {} rt.flatRegionsLayer = null; }
    rt.flatRegionsLayer = L.geoJSON(data.geojson, {
      style: { color: "#fbbf24", weight: 1.5, fillColor: "#fbbf24", fillOpacity: 0.35 },
      onEachFeature: (feature, layer) => {
        const props = feature.properties || {};
        layer.bindTooltip(`${props.areaSqFt ?? "?"} ft² · elongation ${props.elongation ?? "?"}`, { sticky: true });
      },
    }).addTo(app.map.instance);
    rt.flatRegionsCount = data.candidateCount ?? data.geojson?.features?.length ?? 0;
  } catch (err) {
    rt.flatRegionsError = String(err?.message || err);
  } finally {
    rt.flatRegionsBusy = false;
    render();
  }
}

function clearFlatRegions(id) {
  const rt = surfaceRuntimes.get(id);
  if (!rt) return;
  if (rt.flatRegionsLayer) { try { rt.flatRegionsLayer.remove(); } catch {} rt.flatRegionsLayer = null; }
  rt.flatRegionsCount = null;
  rt.flatRegionsError = null;
  render();
}

function resetHistZoom(id) {
  const rt = surfaceRuntimes.get(id);
  if (!rt) return;
  if (rt.histViewFetchTimer) { clearTimeout(rt.histViewFetchTimer); rt.histViewFetchTimer = null; }
  rt.histViewRange = null;
  rt.histViewData = null;
  render();
}

// Mouse-wheel zoom on the histogram itself, centered on the cursor's position — scroll
// up narrows toward a tight band (e.g. a dock deck), scroll down widens back out.
// Rescales the displayed domain immediately for responsiveness and debounces the actual
// finer-histogram refetch so a fast scroll gesture doesn't fire a request per tick.
function onHistWheel(id, e) {
  const s = catalog.find(c => c.id === id);
  const rt = surfaceRuntimes.get(id);
  if (!s || !rt || !s.rasterPath || isClsMosaic(s) || s.settings.colormapName === "solid") return;
  const full = rt.histData;
  if (!full) return;
  e.preventDefault();
  const rect = e.currentTarget.getBoundingClientRect();
  const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / Math.max(1, rect.width)));

  const fullLo = full[1][0], fullHi = full[1][full[1].length - 1];
  const [curLo, curHi] = rt.histViewRange || [fullLo, fullHi];
  const curSpan = curHi - curLo;
  const cursorVal = curLo + pct * curSpan;

  const zoomFactor = e.deltaY < 0 ? 0.85 : 1 / 0.85; // scroll up = zoom in, down = zoom out
  const fullSpan = Math.max(1e-6, fullHi - fullLo);
  const minSpan = Math.max(0.05, fullSpan * 0.002);
  const newSpan = Math.max(minSpan, Math.min(fullSpan, curSpan * zoomFactor));

  if (newSpan >= fullSpan - 1e-9) {
    if (rt.histViewFetchTimer) { clearTimeout(rt.histViewFetchTimer); rt.histViewFetchTimer = null; }
    rt.histViewRange = null;
    rt.histViewData = null;
    render();
    return;
  }
  let newLo = cursorVal - pct * newSpan;
  let newHi = newLo + newSpan;
  if (newLo < fullLo) { newLo = fullLo; newHi = newLo + newSpan; }
  if (newHi > fullHi) { newHi = fullHi; newLo = newHi - newSpan; }
  rt.histViewRange = [newLo, newHi];
  render();

  if (rt.histViewFetchTimer) clearTimeout(rt.histViewFetchTimer);
  rt.histViewFetchTimer = setTimeout(() => {
    rt.histViewFetchTimer = null;
    fetchHistogramView(id, newLo, newHi);
  }, 180);
}

function reapplyZIndices() {
  catalog.forEach((s, i) => {
    const rt = surfaceRuntimes.get(s.id);
    if (!rt) return;
    // Keep in sync with getSurfaceRuntime's initial zBase -- both must stay between tilePane
    // (200) and overlayPane (400) so linework never ends up under a surface.
    const zBase = 210 + Math.min(90, catalog.length - i) * 2;
    rt.paneEl.style.zIndex = String(zBase);
    rt.hillPaneEl.style.zIndex = String(zBase + 1);
  });
}

// ── Histogram rendering ───────────────────────────────────────────────────────

function generateHistogramBars(counts, edges, s) {
  const { colormapName, underflowColor, overflowColor } = s.settings;
  const n = counts.length;
  const maxCount = Math.max(...counts, 1);
  const bw = 100/n;
  if (colormapName === "solid") {
    const lo = edges?.[0] ?? -50;
    const hi = edges?.[edges.length-1] ?? 0;
    const divs = ensureCustomDividers(s, lo, hi);
    return counts.map((c, i) => {
      const pct = (c/maxCount)*100;
      const binCenter = ((edges[i]??0)+(edges[i+1]??0))/2;
      let binIdx = divs.findIndex(d => binCenter < d);
      if (binIdx === -1) binIdx = divs.length;
      const [r,g,b] = s.settings.customColors[Math.min(binIdx, s.settings.customColors.length-1)] || [128,128,128];
      return `<rect x="${(i*bw).toFixed(2)}%" y="${(100-pct).toFixed(2)}%" width="${bw.toFixed(2)}%" height="${pct.toFixed(2)}%" fill="rgb(${r},${g},${b})"/>`;
    }).join("");
  }
  const rMin = s.settings.rescaleMin ?? (edges?.[0] ?? -50);
  const rMax = s.settings.rescaleMax ?? (edges?.[edges.length-1] ?? 0);
  return counts.map((c, i) => {
    const pct = (c/maxCount)*100;
    const binCenter = ((edges[i]??0)+(edges[i+1]??0))/2;
    let fill;
    if (binCenter < rMin) fill = rgbToHex(underflowColor);
    else if (binCenter > rMax) fill = rgbToHex(overflowColor);
    else {
      const t = Math.abs(rMax-rMin) < 1e-10 ? 0.5 : (binCenter-rMin)/(rMax-rMin);
      const [r,g,b] = getColormapColor(t, colormapName);
      fill = `rgb(${r},${g},${b})`;
    }
    return `<rect x="${(i*bw).toFixed(2)}%" y="${(100-pct).toFixed(2)}%" width="${bw.toFixed(2)}%" height="${pct.toFixed(2)}%" fill="${fill}"/>`;
  }).join("");
}

function mergeHistograms(histDataArray) {
  const valid = histDataArray.filter(h => h?.[0]?.length && h?.[1]?.length >= 2);
  if (!valid.length) return null;
  if (valid.length === 1) return valid[0];
  const globalLo = Math.min(...valid.map(h => h[1][0]));
  const globalHi = Math.max(...valid.map(h => h[1][h[1].length - 1]));
  if (globalHi <= globalLo) return valid[0];
  const N = 256;
  const step = (globalHi - globalLo) / N;
  const mergedEdges = Array.from({ length: N + 1 }, (_, i) => globalLo + i * step);
  const mergedCounts = new Array(N).fill(0);
  for (const [counts, edges] of valid) {
    for (let i = 0; i < counts.length; i++) {
      const srcLo = edges[i], srcHi = edges[i + 1];
      const srcWidth = srcHi - srcLo;
      if (srcWidth <= 0 || counts[i] <= 0) continue;
      const firstBin = Math.max(0, Math.floor((srcLo - globalLo) / step));
      const lastBin = Math.min(N - 1, Math.ceil((srcHi - globalLo) / step));
      for (let j = firstBin; j <= lastBin; j++) {
        const overlap = Math.max(0, Math.min(srcHi, mergedEdges[j + 1]) - Math.max(srcLo, mergedEdges[j]));
        mergedCounts[j] += counts[i] * (overlap / srcWidth);
      }
    }
  }
  return [mergedCounts, mergedEdges];
}

// ── Render ────────────────────────────────────────────────────────────────────

function render() {
  if (!container) return;
  const sel = getSelected();

  const btnSty = `background:rgba(148,163,184,0.08);border:1px solid rgba(148,163,184,0.18);color:var(--text);border-radius:4px;padding:2px 9px;font-size:11px;cursor:pointer;`;
  const btnSmSty = `background:rgba(148,163,184,0.08);border:1px solid rgba(148,163,184,0.18);color:var(--text);border-radius:4px;padding:1px 7px;font-size:10px;cursor:pointer;`;
  const dangerSty = `${btnSmSty}color:#f87171;`;

  // ── Catalog rows ──────────────────────────────────────────────────────────
  let catalogRows = "";
  if (loadingCatalog) {
    catalogRows = `<div class="small" style="padding:8px 10px;color:var(--muted);">Loading…</div>`;
  } else if (!catalog.length) {
    catalogRows = `<div class="small" style="padding:8px 10px;color:var(--muted);">No surfaces yet. Click + Add to add one.</div>`;
  } else {
    catalogRows = catalog.map((s, idx) => {
      const vis = visibleIds.has(s.id);
      const isPrimary = s.id === selectedId;
      const isSel = selectedIds.has(s.id);
      const isBuilding = !s.rasterReady;
      const buildError = s.buildJob?.status === "error";
      const topBorder = idx > 0 ? "border-top:1px solid rgba(148,163,184,0.1);" : "";
      const bg = isPrimary ? "background:rgba(56,189,248,0.10);" : isSel ? "background:rgba(56,189,248,0.05);" : "";
      const rt = surfaceRuntimes.get(s.id);
      const { pending, total } = rt?.tileProgressState || { pending:0, total:0 };
      const opPct = Math.round((s.settings?.tileOpacity ?? 1.0) * 100);
      const cmLabel = COLORMAPS.find(c => c.value === s.settings?.colormapName)?.label || s.settings?.colormapName || "";
      const cmGrad = colormapGradientCss(s.settings?.colormapName || "blues_r");

      let statusBadge = "";
      if (buildError) statusBadge = `<span style="font-size:9px;color:#f87171;margin-left:4px;">build error</span>`;
      else if (isBuilding) statusBadge = `<span style="font-size:9px;color:#fbbf24;margin-left:4px;">${esc(s.buildJob?.message||"Building…")} ${s.buildJob?.progressPct > 0 ? s.buildJob.progressPct+"%" : ""}</span>`;

      const tileProgressBar =
        `<div data-surf-tile-wrap="${esc(s.id)}" style="display:${total > 0 ? "block" : "none"};margin:3px 10px 2px;height:2px;border-radius:1px;background:rgba(148,163,184,0.15);overflow:hidden;"><div data-surf-tile-bar="${esc(s.id)}" style="height:100%;background:#38bdf8;width:0%;transition:width 0.15s;"></div></div>`;

      return `<div data-surf-row="${esc(s.id)}" draggable="true" style="${topBorder}${bg}padding:6px 10px 4px;">
        <div style="display:flex;align-items:center;gap:6px;">
          <input type="checkbox" data-surf-vis="${esc(s.id)}" ${vis?"checked":""} ${isBuilding?"disabled":""} style="flex-shrink:0;" />
          <span data-surf-select="${esc(s.id)}" class="small" style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;cursor:pointer;font-weight:${isPrimary?"600":isSel?"500":"400"};color:${isSel?"var(--text)":isBuilding?"var(--muted)":"var(--text)"};" title="${esc(s.name)}">${esc(s.name)}</span>
          ${statusBadge}
          ${!isBuilding ? `
            <div style="width:40px;height:8px;border-radius:2px;background:${cmGrad};flex-shrink:0;opacity:0.8;" title="${esc(cmLabel)}"></div>
            <input type="range" data-surf-opacity="${esc(s.id)}" min="0" max="100" value="${opPct}" style="width:52px;flex-shrink:0;cursor:pointer;" title="Opacity: ${opPct}%" />
            <span data-surf-opacity-label="${esc(s.id)}" class="small" style="color:var(--muted);font-size:9px;min-width:26px;text-align:right;">${opPct}%</span>
          ` : ""}
          <button style="${btnSmSty}" data-surf-rename="${esc(s.id)}" title="Rename">✎</button>
          <button style="${dangerSty}" data-surf-remove="${esc(s.id)}" title="Remove">✕</button>
        </div>
        ${isBuilding && !buildError ? `<div style="margin:4px 0 2px;height:3px;border-radius:2px;background:rgba(148,163,184,0.15);overflow:hidden;"><div style="height:100%;border-radius:2px;background:linear-gradient(90deg,#fbbf24,#f59e0b);width:${s.buildJob?.progressPct??5}%;transition:width 0.5s;"></div></div>` : ""}
        ${tileProgressBar}
      </div>`;
    }).join("");
  }

  // ── Add picker ────────────────────────────────────────────────────────────
  let addPickerHtml = "";
  if (addPickerOpen) {
    // The project's 2D LiDAR mosaic — already rasterized, added without a build step.
    let mosaicRowHtml = "";
    if (mosaicAvailable?.tileCount && !catalog.some(s => s.kind === "mosaic2d" && !s.attr)) {
      mosaicRowHtml = `<div style="padding:5px 10px;border-top:1px solid rgba(148,163,184,0.07);display:flex;align-items:center;gap:8px;">
        <span class="small" style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
          <span style="color:var(--muted);">${esc(activeMarinaCode)} —</span> 2D LiDAR Mosaic
          <span style="font-size:9px;color:#38bdf8;margin-left:4px;">${mosaicAvailable.tileCount} tiles · prebuilt</span>
        </span>
        <button style="${btnSmSty}" id="surf-add-mosaic">Add</button>
      </div>`;
    }
    // Its classification layer (per-class COGs, ASPRS colors) — also prebuilt.
    const clsInfo = mosaicAvailable?.attrs?.classification;
    if (clsInfo?.tileCount && !catalog.some(s => isClsMosaic(s))) {
      mosaicRowHtml += `<div style="padding:5px 10px;border-top:1px solid rgba(148,163,184,0.07);display:flex;align-items:center;gap:8px;">
        <span class="small" style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
          <span style="color:var(--muted);">${esc(activeMarinaCode)} —</span> 2D LiDAR Mosaic — Classification
          <span style="font-size:9px;color:#38bdf8;margin-left:4px;">${clsInfo.tileCount} tiles · classes [${(clsInfo.classes || []).join(", ")}]</span>
        </span>
        <button style="${btnSmSty}" id="surf-add-mosaic-cls">Add</button>
      </div>`;
    }
    // User-defined class-filtered DEMs (e.g. "Bathy Only") from the bulk generator — one row
    // per registered product, skipped once already on the map.
    for (const [key, product] of Object.entries(mosaicProducts)) {
      const stats = mosaicAvailable?.attrs?.[key];
      if (!stats?.tileCount || catalog.some(s => s.kind === "mosaic2d" && s.attr === key)) continue;
      mosaicRowHtml += `<div style="padding:5px 10px;border-top:1px solid rgba(148,163,184,0.07);display:flex;align-items:center;gap:8px;">
        <span class="small" style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
          <span style="color:var(--muted);">${esc(activeMarinaCode)} —</span> ${esc(product.label || key)}
          <span style="font-size:9px;color:#38bdf8;margin-left:4px;">${stats.tileCount} tiles · prebuilt</span>
        </span>
        <button style="${btnSmSty}" data-add-mosaic-product="${esc(key)}">Add</button>
      </div>`;
    }
    if (loadingAvailable) {
      addPickerHtml = `<div style="border-top:1px solid rgba(148,163,184,0.12);background:rgba(2,6,23,0.3);">${mosaicRowHtml}<div style="padding:8px 10px;"><span class="small" style="color:var(--muted);">Loading point files…</span></div></div>`;
    } else if (!available.length) {
      addPickerHtml = `<div style="border-top:1px solid rgba(148,163,184,0.12);background:rgba(2,6,23,0.3);">${mosaicRowHtml}<div style="padding:8px 10px;"><span class="small" style="color:var(--muted);">No point files found in the DAM. Upload LAS/LAZ files in Image Generator → Digital Asset Manager first.</span></div></div>`;
    } else {
      const addedS3Keys = new Set(catalog.map(s => s.s3Key));
      const picks = available.map(av => {
        const alreadyAdded = addedS3Keys.has(av.s3Key);
        const safeKey = esc(av.s3Key);
        return `<div style="padding:5px 10px;border-top:1px solid rgba(148,163,184,0.07);">
          <div style="display:flex;align-items:center;gap:8px;">
            <span class="small" style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${esc(av.fileName)}">
              <span style="color:var(--muted);">${esc(av.marina)} —</span> ${esc(av.name)}
              ${alreadyAdded ? `<span style="font-size:9px;color:#fbbf24;margin-left:4px;">in catalog</span>` : ""}
            </span>
            <button style="${btnSmSty}" data-surf-add-pick="${esc(av.marina)}" data-surf-add-name="${esc(av.name)}" data-surf-add-s3key="${safeKey}" data-surf-add-unit="${esc(av.nativeUnit)}">Add + Build</button>
          </div>
          <div style="display:flex;align-items:center;gap:8px;margin-top:3px;padding-left:2px;">
            <span class="small" style="color:var(--muted);font-size:9px;white-space:nowrap;">Source EPSG:</span>
            <input type="text" class="surf-pick-epsg" data-surf-epsg-key="${safeKey}" placeholder="auto" style="width:56px;font-size:10px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:1px 4px;" />
            <span class="small" style="color:var(--muted);font-size:9px;white-space:nowrap;">Grid res:</span>
            <input type="number" class="surf-pick-res" data-surf-res-key="${safeKey}" required min="0.05" max="5000" step="any" placeholder="e.g. 0.1" style="width:56px;font-size:10px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:1px 4px;" />
            <span class="small" style="color:var(--muted);font-size:9px;">${esc(av.nativeUnit)}</span>
            <span class="small" style="color:var(--muted);font-size:9px;white-space:nowrap;" title="Fill empty cells (e.g. swath gaps) from data up to this many cells away. 0 = leave gaps empty.">Fill gaps:</span>
            <input type="number" class="surf-pick-fill" data-surf-fill-key="${safeKey}" min="0" max="20" step="1" value="0" title="Fill empty cells (e.g. swath gaps) from data up to this many cells away. 0 = leave gaps empty." style="width:38px;font-size:10px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:1px 4px;" />
            <span class="small" style="color:var(--muted);font-size:9px;">cells</span>
          </div>
          <div style="display:flex;align-items:center;gap:8px;margin-top:3px;padding-left:2px;flex-wrap:wrap;">
            <span class="small" style="color:var(--muted);font-size:9px;white-space:nowrap;">Grid stats:</span>
            ${SURFACE_BAND_STATS.map(stat => `
              <label class="small" style="display:flex;align-items:center;gap:3px;font-size:9px;color:var(--muted);cursor:pointer;">
                <input type="checkbox" class="surf-pick-band" data-surf-band-key="${safeKey}" data-surf-band-val="${stat.key}" ${stat.key === "mean" ? "checked" : ""} style="margin:0;" />${stat.label}
              </label>
            `).join("")}
          </div>
        </div>`;
      }).join("");
      addPickerHtml = `<div style="border-top:1px solid rgba(148,163,184,0.12);background:rgba(2,6,23,0.3);max-height:220px;overflow-y:auto;">${mosaicRowHtml}${picks}</div>`;
    }
  }

  // ── Viz controls ──────────────────────────────────────────────────────────
  let vizHtml = "";
  if (sel && sel.rasterReady && isClsMosaic(sel) && selectedIds.size === 1) {
    // Categorical panel: one row per class present in the mosaic (checkbox + swatch + name),
    // doubling as the legend. Continuous controls (colormap/rescale/histogram/hillshade)
    // don't apply to value→color lookups.
    const styles = sel.settings.classStyles || {};
    const classes = presentClasses();
    const anyOverride = Object.keys(styles).length > 0;
    const classRows = classes.map(id => {
      const ov = styles[id] || {};
      const visible = ov.visible !== false;
      const color = ov.color || classDefaultColor(id);
      return `<div style="display:flex;align-items:center;gap:7px;padding:4px 2px;border-bottom:1px solid rgba(148,163,184,0.06);">
        <input type="checkbox" data-surf-class-vis="${id}" ${visible ? "checked" : ""} style="flex-shrink:0;" />
        <div style="position:relative;width:14px;height:14px;border-radius:3px;overflow:hidden;flex-shrink:0;border:1px solid rgba(148,163,184,0.3);" title="Class color">
          <div data-surf-class-swatch="${id}" style="position:absolute;inset:0;background:${esc(color)};opacity:${visible ? 1 : 0.25};"></div>
          <input type="color" data-surf-class-color="${id}" value="${esc(color)}" style="opacity:0;position:absolute;inset:0;width:100%;height:100%;cursor:pointer;border:none;padding:0;" />
        </div>
        <span class="small" style="flex:1;color:${visible ? "var(--text)" : "var(--muted)"};">${id} — ${esc(asprsClassName(id))}</span>
      </div>`;
    }).join("");
    vizHtml = `
    <div style="border-top:2px solid rgba(56,189,248,0.25);margin-top:2px;padding:10px;width:100%;box-sizing:border-box;">
      <div class="small" style="font-weight:600;color:#38bdf8;margin-bottom:6px;">${esc(sel.name)}</div>
      <div class="small" style="color:var(--muted);font-size:9px;margin-bottom:8px;">ASPRS point classes present in this mosaic — the same scheme the 3D viewer uses.</div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:2px;">
        <div class="small" style="color:var(--muted);">Classes (${classes.length})</div>
        <button id="surf-class-reset" style="background:none;border:none;cursor:pointer;font-size:9px;padding:0;line-height:1;color:${anyOverride ? "rgba(148,163,184,0.55)" : "rgba(148,163,184,0.2)"};" title="Restore default colors and visibility">↺ reset</button>
      </div>
      <div style="border:1px solid rgba(148,163,184,0.12);border-radius:5px;padding:2px 8px;margin-bottom:6px;">
        ${classRows || `<div class="small" style="color:var(--muted);padding:6px 0;">No class inventory yet — refresh after the classification batch runs.</div>`}
      </div>
    </div>`;
  } else if (sel && sel.rasterReady) {
    const st = sel.settings;
    const rt = surfaceRuntimes.get(sel.id);
    const isMulti = selectedIds.size > 1;
    if (isMulti) {
      const hists = [];
      for (const id of selectedIds) {
        const rt2 = surfaceRuntimes.get(id);
        if (rt2?.histData) hists.push(rt2.histData);
      }
      lastMergedHistData = mergeHistograms(hists);
    } else {
      lastMergedHistData = null;
    }
    const fullHistData = (isMulti ? lastMergedHistData : rt?.histData) || null;
    const histReadyCount = isMulti ? [...selectedIds].filter(id => surfaceRuntimes.get(id)?.histData).length : 0;
    const isHistZoomed = !isMulti && st.colormapName !== "solid" && !!rt?.histViewRange;
    const histData = isHistZoomed && rt?.histViewData ? rt.histViewData : fullHistData;
    const histLo = isHistZoomed ? rt.histViewRange[0] : (fullHistData?.[1]?.[0] ?? (st.rescaleMin ?? -50));
    const histHi = isHistZoomed ? rt.histViewRange[1] : (fullHistData?.[1]?.[fullHistData[1].length-1] ?? (st.rescaleMax ?? 0));
    const rMin = st.rescaleMin ?? histLo;
    const rMax = st.rescaleMax ?? histHi;
    const range = histHi - histLo || 1;
    const pMin = Math.max(0, Math.min(100, (rMin-histLo)/range*100));
    const pMax = Math.max(0, Math.min(100, (rMax-histLo)/range*100));

    const histSvgInner = histData
      ? generateHistogramBars(histData[0], histData[1], sel)
      : "";
    const histSvgEl = histData
      ? `<svg id="surf-hist-svg" viewBox="0 0 100 52" preserveAspectRatio="none" style="width:100%;height:52px;display:block;">${histSvgInner}</svg>`
      : `<div style="height:52px;display:flex;align-items:center;justify-content:center;"><span class="small" style="color:var(--muted);">${rt?.histFetching?"Loading…":"Histogram unavailable"}</span></div>`;

    const barDivSty = (pct) =>
      `position:absolute;top:0;height:52px;width:2px;background:rgba(255,255,255,0.9);` +
      `cursor:ew-resize;left:${pct.toFixed(2)}%;transform:translateX(-50%);pointer-events:all;box-shadow:0 0 4px rgba(0,0,0,0.6);`;
    const lblFollowSty = (pct) =>
      `position:absolute;top:55px;left:${barLabelLeft(pct)};transform:${barLabelTransform(pct)};` +
      `font-size:8px;color:#e2e8f0;white-space:nowrap;pointer-events:none;text-shadow:0 0 4px #000;`;

    // colormap dropdown
    const cmSelected = COLORMAPS.find(c => c.value === st.colormapName) || COLORMAPS[0];
    const cmGradCurrent = colormapGradientCss(cmSelected.value);
    const cmDropdownHtml = `<div style="position:relative;">
      <button id="surf-cm-trigger" style="display:flex;align-items:center;gap:7px;width:100%;padding:3px 6px;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:4px;cursor:pointer;font-size:11px;color:var(--text);box-sizing:border-box;">
        <span style="display:inline-block;width:52px;height:10px;border-radius:2px;background:${cmGradCurrent};flex-shrink:0;"></span>
        <span style="flex:1;text-align:left;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${esc(cmSelected.label)}</span>
        <span style="color:var(--muted);font-size:9px;">▾</span>
      </button>
      ${cmPickerOpen ? `<div id="surf-cm-dropdown" style="position:absolute;top:calc(100% + 2px);left:0;right:0;z-index:9999;background:rgba(2,6,23,0.97);border:1px solid rgba(148,163,184,0.2);border-radius:4px;overflow-y:auto;max-height:220px;box-shadow:0 8px 24px rgba(0,0,0,0.7);">
        ${COLORMAPS.map(c => {
          const active = st.colormapName === c.value;
          const grad = colormapGradientCss(c.value);
          return `<button data-surf-cm="${esc(c.value)}" style="display:flex;align-items:center;gap:8px;width:100%;padding:5px 8px;background:${active?"rgba(56,189,248,0.1)":"none"};border:none;border-bottom:1px solid rgba(148,163,184,0.06);cursor:pointer;font-size:11px;color:${active?"#38bdf8":"var(--text)"};text-align:left;box-sizing:border-box;">
            <span style="display:inline-block;width:52px;height:10px;border-radius:2px;background:${grad};flex-shrink:0;"></span>
            <span>${esc(c.label)}</span>
          </button>`;
        }).join("")}
      </div>` : ""}
    </div>`;

    // transparent button style
    const transBtnSty = (active) => `background:none;border:none;cursor:pointer;font-size:15px;padding:0 3px;line-height:1;color:${active?"#38bdf8":"rgba(148,163,184,0.4)"};`;

    const sunR = (st.hillshadeAltitude/90)*36;
    const azRad = st.hillshadeAzimuth*Math.PI/180;
    const sunCx = (40+sunR*Math.sin(azRad)).toFixed(1);
    const sunCy = (40-sunR*Math.cos(azRad)).toFixed(1);
    const unit = st.displayUnit || "ft";

    // Polar dial tick marks (16 ticks every 22.5°; cardinals long, intercardinals medium, fine short)
    const sunDialTicks = Array.from({length:16}, (_, i) => {
      const a = i * Math.PI / 8;
      const isCard = i % 4 === 0, isIntercard = i % 2 === 0;
      const r1 = isCard ? 27 : isIntercard ? 30 : 33;
      const x1 = (40 + r1 * Math.sin(a)).toFixed(1), y1 = (40 - r1 * Math.cos(a)).toFixed(1);
      const x2 = (40 + 36 * Math.sin(a)).toFixed(1), y2 = (40 - 36 * Math.cos(a)).toFixed(1);
      const str = isCard ? "rgba(148,163,184,0.55)" : isIntercard ? "rgba(148,163,184,0.3)" : "rgba(148,163,184,0.18)";
      return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${str}" stroke-width="${isCard?"1":"0.6"}"/>`;
    }).join("");

    // Pre-compute histogram + custom legend HTML outside template literal
    const isSolid = st.colormapName === "solid";
    let histogramSectionHtml;
    let customLegendHtml = "";
    if (isSolid) {
      const lo = histLo, hi = histHi, range2 = hi - lo || 1;
      const divs = histData ? ensureCustomDividers(sel, lo, hi) : [];
      const divBarsHtml = divs.map((v, i) => {
        const pct = Math.max(0, Math.min(100, (v - lo) / range2 * 100));
        return `<div id="surf-hist-divider-${i}" style="${barDivSty(pct)}"></div>` +
               `<div id="surf-hist-divider-label-${i}" style="${lblFollowSty(pct)}">${v.toFixed(1)} ${esc(unit)}</div>`;
      }).join("");
      histogramSectionHtml =
        `<div id="surf-hist-wrap" style="position:relative;padding-bottom:20px;margin-bottom:4px;width:100%;">` +
          histSvgEl +
          `<div style="position:absolute;top:0;left:0;right:0;overflow:visible;pointer-events:none;">${divBarsHtml}</div>` +
        `</div>` +
        `<div style="display:flex;justify-content:space-between;font-size:8px;color:#475569;padding:0 2px;margin-bottom:6px;">` +
          `<span>${lo.toFixed(1)} ${esc(unit)}</span><span>${hi.toFixed(1)} ${esc(unit)}</span>` +
        `</div>`;
      const checkeredBg = "repeating-conic-gradient(#aaa 0% 25%,#fff 0% 50%) 0 0/8px 8px";
      const mono = "font-size:11px;font-family:monospace;color:#94a3b8;";
      const uspan = `<span style="font-size:10px;color:#475569;margin-left:2px;">${esc(unit)}</span>`;
      const ps = "position:absolute;top:0;left:0;width:100%;height:100%;opacity:0;cursor:pointer;border:none;padding:0;";
      const tbSty = (act) => `background:none;border:none;padding:0 0 0 2px;cursor:pointer;font-size:10px;line-height:1;color:${act?"#38bdf8":"#475569"};flex-shrink:0;`;
      const headerRow =
        `<div style="display:grid;grid-template-columns:34px 1fr 1fr;border-bottom:1px solid rgba(148,163,184,0.1);background:rgba(2,6,23,0.35);">` +
        `<div></div>` +
        `<div style="padding:2px 6px;font-size:9px;color:#475569;text-transform:uppercase;letter-spacing:0.07em;">From</div>` +
        `<div style="padding:2px 6px;font-size:9px;color:#475569;text-transform:uppercase;letter-spacing:0.07em;">To</div>` +
        `</div>`;
      const binRows = st.customColors.slice(0, st.customBins).map((color, i) => {
        const hex = esc(rgbToHex(color));
        const isTransp = st.customTransparent[i] || false;
        const binLo = i === 0 ? lo : (divs[i-1] ?? lo);
        const binHi = i === st.customBins - 1 ? hi : (divs[i] ?? hi);
        const isLast = i === st.customBins - 1;
        const rb = i > 0 ? "border-top:1px solid rgba(148,163,184,0.06);" : "";
        return (
          `<div style="display:grid;grid-template-columns:34px 1fr 1fr;align-items:center;${rb}">` +
          `<div style="display:flex;align-items:center;justify-content:center;gap:1px;">` +
            `<div style="position:relative;width:12px;height:12px;border-radius:2px;overflow:hidden;cursor:pointer;" title="Pick color">` +
              `<div id="surf-custom-swatch-${i}" style="width:12px;height:12px;background:${isTransp?checkeredBg:hex};"></div>` +
              `<input type="color" id="surf-custom-picker-${i}" value="${hex}" style="${ps}"/>` +
            `</div>` +
            `<button id="surf-custom-transp-${i}" style="${tbSty(isTransp)}">∅</button>` +
          `</div>` +
          `<div style="padding:3px 6px;"><span id="surf-custom-from-${i}" style="${mono}">${binLo.toFixed(1)}</span>${uspan}</div>` +
          `<div style="padding:3px 6px;">${isLast
            ? `<span style="${mono}">${binHi.toFixed(1)}</span>${uspan}`
            : `<input id="surf-custom-to-${i}" type="number" step="0.1" value="${binHi.toFixed(1)}" style="width:60px;font-size:11px;font-family:monospace;"/>${uspan}`
          }</div>` +
          `</div>`
        );
      }).join("");
      customLegendHtml = `<div style="border:1px solid rgba(148,163,184,0.12);border-radius:5px;overflow:hidden;margin-bottom:8px;">${headerRow}${binRows}</div>`;
    } else {
      const uCol = st.underflowTransparent ? "repeating-conic-gradient(#555 0% 25%,#888 0% 50%) 0 0/6px 6px" : rgbToHex(st.underflowColor);
      const oCol = st.overflowTransparent ? "repeating-conic-gradient(#555 0% 25%,#888 0% 50%) 0 0/6px 6px" : rgbToHex(st.overflowColor);
      const swatchSty = "position:relative;width:11px;height:11px;border-radius:2px;overflow:hidden;cursor:pointer;flex-shrink:0;border:1px solid rgba(148,163,184,0.25);";
      const transBtnSmSty = (act) =>
        `display:inline-flex;align-items:center;justify-content:center;width:12px;height:12px;border-radius:2px;` +
        `cursor:pointer;border:1px solid rgba(148,163,184,0.3);padding:0;flex-shrink:0;font-size:8px;line-height:1;` +
        `background:${act?"repeating-conic-gradient(#555 0% 25%,#888 0% 50%) 0 0/6px 6px":"rgba(15,23,42,0.5)"};` +
        `color:${act?"#38bdf8":"rgba(148,163,184,0.4)"};`;
      const zoomToggleHtml = sel.kind !== "mosaic2d"
        ? (isHistZoomed
            ? `<button id="surf-hist-zoom-reset" style="position:absolute;top:2px;left:50%;transform:translateX(-50%);font-size:8px;padding:1px 6px;background:rgba(56,189,248,0.15);border:1px solid rgba(56,189,248,0.35);border-radius:3px;color:#38bdf8;cursor:pointer;z-index:2;pointer-events:all;" title="Show the full elevation range">↺ full range</button>`
            : `<span style="position:absolute;top:3px;left:50%;transform:translateX(-50%);font-size:8px;color:rgba(148,163,184,0.4);pointer-events:none;z-index:2;">scroll to zoom</span>`)
        : "";
      histogramSectionHtml =
        `<div id="surf-hist-wrap" style="position:relative;padding-bottom:24px;margin-bottom:8px;width:100%;">` +
          histSvgEl +
          zoomToggleHtml +
          `<div id="surf-hist-bar-min" style="position:absolute;top:0;height:52px;width:14px;background:transparent;cursor:ew-resize;left:${pMin.toFixed(2)}%;transform:translateX(-50%);pointer-events:all;">` +
            `<div style="position:absolute;left:6px;top:0;bottom:0;width:2px;background:rgba(255,255,255,0.9);box-shadow:0 0 4px rgba(0,0,0,0.6);"></div>` +
            `<div style="position:absolute;left:3px;bottom:0;width:8px;height:8px;border-radius:50%;background:#e2e8f0;box-shadow:0 0 4px rgba(0,0,0,0.6);"></div>` +
            `<div id="surf-hist-label-min" style="position:absolute;top:54px;left:50%;transform:translateX(-50%);font-size:8px;color:#e2e8f0;white-space:nowrap;background:rgba(2,6,23,0.85);padding:1px 4px;border-radius:3px;border:1px solid rgba(255,255,255,0.15);pointer-events:none;z-index:3;">${rMin.toFixed(1)} ${esc(unit)}</div>` +
          `</div>` +
          `<div id="surf-hist-bar-max" style="position:absolute;top:0;height:52px;width:14px;background:transparent;cursor:ew-resize;left:${pMax.toFixed(2)}%;transform:translateX(-50%);pointer-events:all;">` +
            `<div style="position:absolute;left:6px;top:0;bottom:0;width:2px;background:rgba(255,255,255,0.9);box-shadow:0 0 4px rgba(0,0,0,0.6);"></div>` +
            `<div style="position:absolute;left:3px;bottom:0;width:8px;height:8px;border-radius:50%;background:#e2e8f0;box-shadow:0 0 4px rgba(0,0,0,0.6);"></div>` +
            `<div id="surf-hist-label-max" style="position:absolute;top:54px;left:50%;transform:translateX(-50%);font-size:8px;color:#e2e8f0;white-space:nowrap;background:rgba(2,6,23,0.85);padding:1px 4px;border-radius:3px;border:1px solid rgba(255,255,255,0.15);pointer-events:none;z-index:3;">${rMax.toFixed(1)} ${esc(unit)}</div>` +
          `</div>` +
          `<div style="position:absolute;top:2px;left:2px;display:flex;align-items:center;gap:1px;pointer-events:all;z-index:2;">` +
            `<div style="${swatchSty}" title="Underflow color">` +
              `<div style="position:absolute;inset:0;background:${uCol};"></div>` +
              `<input id="surf-underflow-picker" type="color" value="${rgbToHex(st.underflowColor)}" style="opacity:0;position:absolute;inset:0;width:100%;height:100%;cursor:pointer;border:none;padding:0;" />` +
            `</div>` +
            `<button id="surf-underflow-transp" style="${transBtnSmSty(st.underflowTransparent)}" title="Toggle transparent">⌀</button>` +
          `</div>` +
          `<div style="position:absolute;top:2px;right:2px;display:flex;align-items:center;gap:1px;pointer-events:all;z-index:2;">` +
            `<button id="surf-overflow-transp" style="${transBtnSmSty(st.overflowTransparent)}" title="Toggle transparent">⌀</button>` +
            `<div style="${swatchSty}" title="Overflow color">` +
              `<div style="position:absolute;inset:0;background:${oCol};"></div>` +
              `<input id="surf-overflow-picker" type="color" value="${rgbToHex(st.overflowColor)}" style="opacity:0;position:absolute;inset:0;width:100%;height:100%;cursor:pointer;border:none;padding:0;" />` +
            `</div>` +
          `</div>` +
        `</div>`;
    }

    // Dock centerline tool — draw a line, auto-tune the rescale/mask range from it. Works on
    // the mosaic too (not just single Qimera COGs) so the mask it builds can be baked into
    // the mosaic's GeoTIFF export below — classification is categorical, so excluded.
    let dockLineSectionHtml = "";
    if (!isMulti && !isClsMosaic(sel)) {
      const dockSamples = getDockLineSamples(sel.id);
      const dockSampleRows = dockSamples.map((sm, i) => `
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:2px;">
          <span class="small" style="flex:1;font-size:10px;">Sample ${i + 1}: ${sm.topOfDeck.toFixed(2)} ${esc(unit)} top-of-deck (${sm.count} px)</span>
          <button data-dockline-remove="${esc(sm.id)}" style="font-size:9px;padding:1px 5px;background:none;border:1px solid rgba(248,113,113,0.35);border-radius:3px;color:#f87171;cursor:pointer;" title="Remove this sample">×</button>
        </div>`).join("");
      dockLineSectionHtml = `
        <div style="border-top:1px solid rgba(148,163,184,0.12);margin-top:8px;padding-top:8px;">
          <div class="small" style="font-weight:600;margin-bottom:4px;">Dock Centerline</div>
          <div class="small" style="color:var(--muted);font-size:10px;margin-bottom:6px;">
            ${dockLineActive ? "Click two points along the dock's centerline on the map." : "Draw a line down a dock's centerline to auto-snap the rescale range (and mask) to its deck elevation. Repeat on other docks if elevation varies — the mask widens to cover every sample."}
          </div>
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:4px;">
            <button id="surf-dockline-draw" style="${btnSmSty}${dockLineActive ? "color:#fbbf24;" : ""}" ${dockLineBusy ? "disabled" : ""}>${dockLineActive ? "✕ Cancel" : "📏 Draw Dock Centerline"}</button>
            <label class="small" style="display:flex;align-items:center;gap:4px;font-size:10px;">Width
              <input id="surf-dockline-width" type="number" min="0.2" step="0.1" value="${esc(String(dockLineWidth))}" style="width:52px;font-size:10px;" /> ${esc(unit)}
            </label>
            <label class="small" style="display:flex;align-items:center;gap:4px;font-size:10px;">Tolerance ±
              <input id="surf-dockline-tolerance" type="number" min="0.05" step="0.05" value="${esc(String(dockLineTolerance))}" style="width:52px;font-size:10px;" /> ${esc(unit)}
            </label>
          </div>
          ${dockLineBusy ? `<div class="small" style="color:#38bdf8;font-size:10px;">Sampling raster along that line…</div>` : ""}
          ${dockLineError ? `<div class="small" style="color:#f87171;font-size:10px;">${esc(dockLineError)}</div>` : ""}
          ${dockSamples.length ? `
            <div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(148,163,184,0.1);">
              <div class="small" style="color:var(--muted);font-size:9px;margin-bottom:4px;">${dockSamples.length} sample${dockSamples.length === 1 ? "" : "s"} — mask is the union of all of them (lowest low to highest high), not their average.</div>
              ${dockSampleRows}
              <div class="small" style="margin-top:4px;font-weight:600;font-size:10px;">Combined mask: ${(st.rescaleMin ?? 0).toFixed(2)}–${(st.rescaleMax ?? 0).toFixed(2)} ${esc(unit)}</div>
              <button id="surf-dockline-clear" style="font-size:9px;padding:2px 8px;margin-top:4px;background:none;border:1px solid rgba(148,163,184,0.2);border-radius:3px;color:var(--muted);cursor:pointer;">Clear all samples</button>
            </div>
          ` : ""}
        </div>`;
    }

    // Pre-compute adjuster slider stack HTML (with/without Z-factor)
    const zFactor    = st.tileZFactor    ?? 1.0;
    const saturation = st.tileSaturation ?? 1.0;
    const contrast   = st.tileContrast   ?? 1.0;
    const slrRow = (lbl, id, val, disp, mn, mx) =>
      `<div style="display:flex;align-items:center;gap:5px;margin-bottom:6px;">` +
      `<span style="font-size:9px;color:var(--muted);white-space:nowrap;min-width:54px;">${lbl}</span>` +
      `<input id="${id}" type="range" min="${mn}" max="${mx}" value="${val}" style="flex:1;min-width:0;margin:0;" />` +
      `<span id="${id}-val" style="font-size:9px;color:var(--text);font-family:monospace;min-width:26px;text-align:right;white-space:nowrap;">${disp}</span>` +
      `</div>`;
    const adjDefaulted = zFactor === 1.0 && saturation === 1.0 && contrast === 1.0 && st.tileGamma === 1.0;
    const adjSliders = (withZ) =>
      `<div style="flex:1;min-width:0;display:flex;flex-direction:column;justify-content:center;">` +
      `<div style="display:flex;justify-content:flex-end;margin-bottom:1px;">` +
      `<button id="surf-adj-reset" style="background:none;border:none;cursor:pointer;font-size:9px;padding:0;line-height:1;color:${adjDefaulted?"rgba(148,163,184,0.2)":"rgba(148,163,184,0.55)"};" title="Reset to defaults">↺ reset</button>` +
      `</div>` +
      (withZ ? slrRow("Z-factor","surf-zfactor",Math.round(zFactor*10),zFactor.toFixed(1)+"×",10,100) : "") +
      slrRow("Saturation","surf-saturation",Math.round(saturation*100),saturation.toFixed(2),0,300) +
      slrRow("Contrast","surf-contrast",Math.round(contrast*100),contrast.toFixed(2),0,300) +
      slrRow("Gamma","surf-gamma",Math.round(st.tileGamma*100),st.tileGamma.toFixed(2),10,300) +
      `</div>`;

    vizHtml = `
    <div style="border-top:2px solid rgba(56,189,248,0.25);margin-top:2px;padding:10px;width:100%;box-sizing:border-box;">
      <div class="small" style="font-weight:600;color:#38bdf8;margin-bottom:${selectedIds.size > 1 ? "3" : "6"}px;">${esc(sel.name)}</div>
      ${selectedIds.size > 1 ? `<div class="small" style="color:#94a3b8;font-size:9px;margin-bottom:6px;">${selectedIds.size} surfaces selected — settings apply to all</div>` : ""}
      ${selectedIds.size === 1 && sel.kind === "mosaic2d" ? `<div style="display:flex;align-items:center;gap:5px;margin-bottom:8px;">
        <span class="small" style="color:var(--muted);font-size:9px;">Project-wide LiDAR mosaic — tiles are generated in the Potree Bulk Generator.</span>
      </div>` : ""}
      ${selectedIds.size === 1 && sel.kind !== "mosaic2d" ? (() => {
        const gridRes = sel.resolution ?? 1;
        const gridUnit = sel.settings.displayUnit || "ft";
        if (rebuildOpen) {
          return `<div style="background:rgba(0,0,0,0.25);border-radius:6px;padding:7px 8px;margin-bottom:8px;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;flex-wrap:wrap;">
              <span class="small" style="color:var(--muted);white-space:nowrap;font-size:9px;">EPSG:</span>
              <input id="surf-rebuild-epsg" type="text" value="${esc(sel.sourceEpsg||"")}" placeholder="auto" style="width:56px;font-size:10px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:1px 4px;" />
              <span class="small" style="color:var(--muted);white-space:nowrap;font-size:9px;">Res:</span>
              <input id="surf-rebuild-res" type="number" required min="0.05" max="5000" step="any" value="${gridRes}" placeholder="e.g. 0.1" style="width:56px;font-size:10px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:1px 4px;" />
              <span class="small" style="color:var(--muted);font-size:9px;">${gridUnit}</span>
              <span class="small" style="color:var(--muted);white-space:nowrap;font-size:9px;" title="Fill empty cells (e.g. swath gaps) from data up to this many cells away. 0 = leave gaps empty.">Fill gaps:</span>
              <input id="surf-rebuild-fill" type="number" min="0" max="20" step="1" value="${Number(sel.fillGapCells) || 0}" title="Fill empty cells (e.g. swath gaps) from data up to this many cells away. 0 = leave gaps empty." style="width:38px;font-size:10px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:1px 4px;" />
              <span class="small" style="color:var(--muted);font-size:9px;">cells</span>
            </div>
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;flex-wrap:wrap;">
              <span class="small" style="color:var(--muted);font-size:9px;white-space:nowrap;">Grid stats:</span>
              ${SURFACE_BAND_STATS.map(stat => `
                <label class="small" style="display:flex;align-items:center;gap:3px;font-size:9px;color:var(--muted);cursor:pointer;">
                  <input type="checkbox" id="surf-rebuild-band-${stat.key}" data-surf-band-val="${stat.key}" ${(sel.bands?.length ? sel.bands.includes(stat.key) : stat.key === "mean") ? "checked" : ""} style="margin:0;" />${stat.label}
                </label>
              `).join("")}
            </div>
            <div style="display:flex;gap:5px;">
              <button id="surf-rebuild-go" style="font-size:10px;padding:2px 8px;background:rgba(56,189,248,0.15);border:1px solid rgba(56,189,248,0.35);border-radius:4px;color:#38bdf8;cursor:pointer;">↺ Rebuild</button>
              <button id="surf-rebuild-cancel" style="font-size:10px;padding:2px 8px;background:none;border:1px solid rgba(148,163,184,0.2);border-radius:4px;color:var(--muted);cursor:pointer;">Cancel</button>
            </div>
          </div>`;
        } else {
          // First open converts the source LAS for 3D streaming (minutes for a large file):
          // show the phase, elapsed time and source size so it doesn't look stuck.
          const fmtElapsed = (t) => t == null ? "" : ` ${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
          const fmtBytes = (b) => !b ? "" : b >= 1e9 ? ` ${(b / 1e9).toFixed(1)} GB` : ` ${Math.round(b / 1e6)} MB`;
          const open3dLabel = sel.open3dStatus === "ready" ? (sel.open3dBlocked ? "🧊 3D view ready — click to open ↗" : "🧊 Open in 3D ↗")
            : sel.open3dStatus === "failed" ? "🧊 3D build failed — retry"
            : sel.open3dStatus ? `🧊 ${sel.open3dRestarted ? "restarted, " : ""}${esc(sel.open3dStatus)}${fmtBytes(sel.open3dBytes)}…${fmtElapsed(sel.open3dElapsed)}`
            : "🧊 Open in 3D";
          const open3dTitle = sel.open3dStatus === "failed" && sel.open3dError ? `3D build failed: ${sel.open3dError}`
            : sel.open3dStatus && sel.open3dStatus !== "ready" ? "Converting this surface's source LAS for 3D streaming (first open only; reopening is instant). A large file can take several minutes."
            : "Open a tokenized 3D view of this surface's own source point cloud (proof of concept)";
          const open3dBusy = !!sel.open3dStatus && sel.open3dStatus !== "ready" && sel.open3dStatus !== "failed";
          return `<div style="display:flex;align-items:center;gap:5px;margin-bottom:8px;flex-wrap:wrap;">
            <span class="small" style="color:var(--muted);font-size:9px;">Grid:</span>
            <span class="small" style="font-size:9px;font-family:monospace;">${gridRes} ${gridUnit}</span>
            <button id="surf-rebuild-toggle" style="font-size:9px;padding:1px 5px;background:none;border:1px solid rgba(148,163,184,0.2);border-radius:3px;color:var(--muted);cursor:pointer;margin-left:2px;">⚙ Rebuild</button>
            <button data-surf-zoom="${esc(sel.id)}" title="Zoom the map to this surface's extent" ${sel.rasterReady ? "" : "disabled"} style="font-size:9px;padding:1px 5px;background:none;border:1px solid rgba(148,163,184,0.2);border-radius:3px;color:var(--muted);cursor:${sel.rasterReady ? "pointer" : "default"};margin-left:2px;">⤢ Zoom Extents</button>
            <button data-surf-open3d="${esc(sel.id)}" title="${esc(open3dTitle)}" style="font-size:9px;padding:1px 5px;background:${sel.open3dBlocked && sel.open3dStatus === "ready" ? "rgba(56,189,248,0.15)" : "none"};border:1px solid ${sel.open3dBlocked && sel.open3dStatus === "ready" ? "rgba(56,189,248,0.5)" : "rgba(148,163,184,0.2)"};border-radius:3px;color:${sel.open3dStatus === "ready" ? "#38bdf8" : "var(--muted)"};cursor:${open3dBusy ? "default" : "pointer"};margin-left:2px;"${open3dBusy ? " disabled" : ""}>${open3dLabel}</button>
          </div>`;
        }
      })() : ""}

      ${selectedIds.size === 1 && sel.bands?.length > 1 ? `<div style="display:flex;align-items:center;gap:5px;margin-bottom:8px;">
        <span class="small" style="color:var(--muted);font-size:9px;">Grid statistic:</span>
        <select id="surf-display-band" style="font-size:10px;padding:1px 4px;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);">
          ${SURFACE_BAND_STATS.filter(stat => sel.bands.includes(stat.key)).map(stat =>
            `<option value="${stat.key}" ${sel.settings.displayBand === stat.key ? "selected" : ""}>${stat.label}</option>`
          ).join("")}
        </select>
      </div>` : ""}

      ${selectedIds.size === 1 && sel.bands?.includes("stdev") ? (() => {
        const frt = getSurfaceRuntime(sel.id);
        if (!frt.flatRegionsOpen) {
          return `<div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;flex-wrap:wrap;">
            <button id="surf-flat-toggle" style="font-size:9px;padding:1px 5px;background:none;border:1px solid rgba(148,163,184,0.2);border-radius:3px;color:var(--muted);cursor:pointer;">⬚ Find Flat Regions (beta)</button>
            ${frt.flatRegionsCount != null ? `<span class="small" style="font-size:9px;color:#fbbf24;">${frt.flatRegionsCount} candidate${frt.flatRegionsCount === 1 ? "" : "s"}</span><button id="surf-flat-clear" style="font-size:9px;padding:0 3px;background:none;border:none;color:var(--muted);cursor:pointer;" title="Clear overlay">✕</button>` : ""}
          </div>`;
        }
        const p = frt.flatRegionsParams || { stdevMax: 0.5, minAreaSqFt: 200, minElongation: 2 };
        return `<div style="background:rgba(0,0,0,0.25);border-radius:6px;padding:7px 8px;margin-bottom:8px;">
          <div class="small" style="color:var(--muted);font-size:9px;margin-bottom:6px;">Thresholds the Std Dev band, then keeps only large, elongated flat regions — a first-pass dock candidate detector, not a saved result.</div>
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;flex-wrap:wrap;">
            <span class="small" style="color:var(--muted);font-size:9px;">Std Dev ≤</span>
            <input id="surf-flat-stdev" type="number" step="0.05" min="0.01" value="${p.stdevMax}" style="width:56px;font-size:10px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:1px 4px;" />
            <span class="small" style="color:var(--muted);font-size:9px;">Min area</span>
            <input id="surf-flat-area" type="number" step="10" min="1" value="${p.minAreaSqFt}" style="width:56px;font-size:10px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:1px 4px;" /><span class="small" style="color:var(--muted);font-size:9px;">ft²</span>
            <span class="small" style="color:var(--muted);font-size:9px;">Min elongation</span>
            <input id="surf-flat-elong" type="number" step="0.5" min="1" value="${p.minElongation}" style="width:48px;font-size:10px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:1px 4px;" />
          </div>
          ${frt.flatRegionsError ? `<div class="small" style="color:#f87171;font-size:9px;margin-bottom:6px;">${esc(frt.flatRegionsError)}</div>` : ""}
          <div style="display:flex;gap:5px;align-items:center;">
            <button id="surf-flat-go" ${frt.flatRegionsBusy ? "disabled" : ""} style="font-size:10px;padding:2px 8px;background:rgba(251,191,36,0.15);border:1px solid rgba(251,191,36,0.4);border-radius:4px;color:#fbbf24;cursor:${frt.flatRegionsBusy ? "default" : "pointer"};opacity:${frt.flatRegionsBusy ? 0.6 : 1};">${frt.flatRegionsBusy ? "Running…" : "▶ Run"}</button>
            <button id="surf-flat-cancel" style="font-size:10px;padding:2px 8px;background:none;border:1px solid rgba(148,163,184,0.2);border-radius:4px;color:var(--muted);cursor:pointer;">Close</button>
            ${frt.flatRegionsCount != null ? `<span class="small" style="font-size:9px;color:#fbbf24;">${frt.flatRegionsCount} candidate${frt.flatRegionsCount === 1 ? "" : "s"}</span>` : ""}
          </div>
        </div>`;
      })() : ""}

      <!-- Colormap row -->
      <div style="display:flex;gap:10px;align-items:flex-end;margin-bottom:8px;">
        <div style="flex:1;min-width:0;">
          <div class="small" style="color:var(--muted);margin-bottom:4px;">Colormap</div>
          ${cmDropdownHtml}
        </div>
        ${isSolid ? `<div style="flex-shrink:0;"><div class="small" style="color:var(--muted);margin-bottom:4px;">Bins</div><input id="surf-custom-bins" type="number" min="2" max="8" value="${st.customBins}" style="width:48px;font-size:11px;" /></div>` : ""}
      </div>

      <!-- Histogram -->
      ${isMulti ? `<div style="font-size:9px;color:#94a3b8;margin-bottom:3px;">Combined histogram · ${histReadyCount} of ${selectedIds.size} surfaces loaded</div>` : ""}
      ${histogramSectionHtml}
      ${customLegendHtml}
      ${dockLineSectionHtml}

      <!-- Hillshade + adjuster stack -->
      <div style="margin-bottom:4px;">
        <label class="small"><input id="surf-hillshade" type="checkbox" ${st.hillshadeOverlay?"checked":""} style="margin-right:5px;" />Hillshade</label>
      </div>
      ${st.hillshadeOverlay ? `
      <div style="display:flex;gap:8px;align-items:stretch;margin-bottom:6px;">
        <div style="padding:6px;background:rgba(0,0,0,0.2);border-radius:6px;flex-shrink:0;display:flex;flex-direction:column;align-items:center;justify-content:center;">
          <svg id="surf-sun-svg" viewBox="0 0 80 80" style="width:70px;height:70px;cursor:crosshair;display:block;">
            <circle cx="40" cy="40" r="38" fill="rgba(15,23,42,0.8)" stroke="rgba(148,163,184,0.25)" stroke-width="1"/>
            <circle cx="40" cy="40" r="12" fill="none" stroke="rgba(148,163,184,0.12)" stroke-width="0.5" stroke-dasharray="2,3"/>
            <circle cx="40" cy="40" r="24" fill="none" stroke="rgba(148,163,184,0.15)" stroke-width="0.5" stroke-dasharray="2,3"/>
            <circle cx="40" cy="40" r="36" fill="none" stroke="rgba(148,163,184,0.25)" stroke-width="0.5"/>
            ${sunDialTicks}
            <text x="40" y="10" text-anchor="middle" font-size="7" fill="rgba(148,163,184,0.8)" font-weight="600">N</text>
            <text x="40" y="76" text-anchor="middle" font-size="7" fill="rgba(148,163,184,0.45)">S</text>
            <text x="8"  y="43" text-anchor="middle" font-size="7" fill="rgba(148,163,184,0.45)">W</text>
            <text x="72" y="43" text-anchor="middle" font-size="7" fill="rgba(148,163,184,0.45)">E</text>
            <text x="54" y="43" font-size="4.5" fill="rgba(148,163,184,0.28)">30°</text>
            <text x="66" y="43" font-size="4.5" fill="rgba(148,163,184,0.22)">60°</text>
            <circle id="surf-sun-glow" cx="${sunCx}" cy="${sunCy}" r="9" fill="rgba(251,191,36,0.2)"/>
            <circle id="surf-sun-dot"  cx="${sunCx}" cy="${sunCy}" r="5" fill="#fbbf24" stroke="rgba(255,255,255,0.9)" stroke-width="1.5"/>
          </svg>
          <div id="surf-sun-label" class="small" style="text-align:center;color:var(--muted);font-size:9px;margin-top:3px;">Az ${st.hillshadeAzimuth}° · Alt ${st.hillshadeAltitude}°</div>
        </div>
        ${adjSliders(true)}
      </div>
      ` : `<div style="margin-bottom:6px;">${adjSliders(false)}</div>`}
    </div>`;
  } else if (sel && !sel.rasterReady) {
    const isError = sel.buildJob?.status === "error" || sel.buildJob?.status === "failed";
    // A refused CRS check (unattended auto-build) arrives on the poll's job, or on the catalog
    // entry when the page loads after the fact -- either way, offer its suggested EPSG.
    const crsFail = sel.buildJob?.crsCheck || sel.crsCheck || null;
    vizHtml = `
    <div style="border-top:2px solid rgba(148,163,184,0.15);margin-top:2px;padding:10px;width:100%;box-sizing:border-box;">
      <div class="small" style="font-weight:600;color:var(--muted);margin-bottom:6px;">${esc(sel.name)}</div>
      ${isError
        ? `${crsFail
              ? `<div class="small" style="color:#fbbf24;font-weight:600;margin-bottom:3px;">⚠ Not built: CRS doesn't match this marina</div>
                 <div class="small" style="margin-bottom:6px;line-height:1.4;">${esc(crsFail.message || "")}</div>`
              : `<div class="small" style="color:#f87171;margin-bottom:6px;">Build failed: ${esc(sel.buildJob?.message||"unknown error")}</div>`}
           <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
             <span class="small" style="color:var(--muted);white-space:nowrap;">Source EPSG:</span>
             <input id="surf-retry-epsg" type="text" value="${esc(String((crsFail?.recommended || sel.sourceEpsg) || "").replace(/^EPSG:/i, ""))}" placeholder="e.g. 2227" style="width:80px;font-size:11px;font-family:monospace;background:rgba(15,23,42,0.5);border:1px solid rgba(148,163,184,0.25);border-radius:3px;color:var(--text);padding:2px 5px;" title="EPSG code for source CRS (e.g. 2227 = CA State Plane Zone III US Feet)" />
           </div>
           <button id="surf-retry-build" style="${btnSty}">Retry Build</button>`
        : `<div class="small" style="color:#fbbf24;">Building raster… ${sel.buildJob?.progressPct?sel.buildJob.progressPct+"%":""}</div>
           <div class="small" style="color:var(--muted);margin-top:2px;">${esc(sel.buildJob?.message||"Processing point cloud…")}</div>
           <div style="margin-top:8px;height:4px;border-radius:2px;background:rgba(148,163,184,0.15);overflow:hidden;"><div style="height:100%;border-radius:2px;background:linear-gradient(90deg,#fbbf24,#f59e0b);width:${sel.buildJob?.progressPct??5}%;transition:width 0.5s;"></div></div>
           <div class="small" style="color:var(--muted);margin-top:6px;font-size:10px;">Visualization controls will appear when the raster is ready.</div>`
      }
    </div>`;
  }

  // ── GeoTIFF export section (mosaic surfaces and built single-COG surfaces) ────
  let exportHtml = "";
  const isExportable = sel && sel.rasterReady && (sel.kind === "mosaic2d" || sel.rasterPath) && selectedIds.size === 1;
  if (isExportable) {
    const isMosaicExport = sel.kind === "mosaic2d";
    const attrLabel = isClsMosaic(sel) ? "classification (uint8 class codes)" : "elevation (float32, meters)";
    const jobActive = exportJob && (exportJob.status === "queued" || exportJob.status === "running");
    const areaLabel = exportDrawing
      ? `Drawing… ${exportDrawPoints.length} ${exportDrawPoints.length === 1 ? "vertex" : "vertices"}`
      : exportPolygon
        ? `Drawn area · ${exportPolygon.length} vertices`
        : (isMosaicExport ? "Whole mosaic" : "Whole surface");
    const maskRangeForDisplay = activeMaskRange(sel);
    const dispUnit = sel.settings.displayUnit || "ft";
    const maskHtml = maskRangeForDisplay ? `
      <label class="small" style="display:flex;align-items:center;gap:6px;margin-top:6px;font-size:10px;">
        <input id="surf-export-mask" type="checkbox" ${exportApplyMask ? "checked" : ""} ${jobActive ? "disabled" : ""} />
        Mask to dock elevations (${maskRangeForDisplay.min.toFixed(2)}–${maskRangeForDisplay.max.toFixed(2)} ${esc(dispUnit)}) — everything outside that range is written as nodata
      </label>` : "";
    let jobHtml = "";
    if (jobActive) {
      jobHtml = `
        <div class="small" style="color:#fbbf24;margin-top:6px;">${esc(exportJob.message || "Exporting…")}</div>
        <div style="margin-top:4px;height:3px;border-radius:2px;background:rgba(148,163,184,0.15);overflow:hidden;"><div style="height:100%;border-radius:2px;background:linear-gradient(90deg,#38bdf8,#22c55e);width:${exportJob.progressPct ?? 5}%;transition:width 0.5s;"></div></div>`;
    } else if (exportJob?.status === "completed") {
      jobHtml = `<div class="small" style="color:#22c55e;margin-top:6px;">✓ ${esc(exportJob.message || "Export ready in the DAM.")}</div>`;
    } else if (exportJob?.status === "failed") {
      jobHtml = `<div class="small" style="color:#f87171;margin-top:6px;">Export failed: ${esc(exportJob.error || exportJob.message || "unknown error")}</div>`;
    }
    exportHtml = `
    <div style="border-top:2px solid rgba(148,163,184,0.15);margin-top:2px;padding:10px;width:100%;box-sizing:border-box;">
      <div class="small" style="font-weight:600;color:var(--muted);margin-bottom:4px;">EXPORT GEOTIFF</div>
      <div class="small" style="color:var(--muted);font-size:10px;margin-bottom:6px;">${isMosaicExport ? "Merges the mosaic tiles into one data GeoTIFF" : "Exports this surface's raster as a data GeoTIFF"} — ${esc(attrLabel)} — and saves it to the DAM under geotiff-files/.</div>
      <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
        <button id="surf-export-draw" style="${btnSmSty}${exportDrawing ? "color:#fbbf24;" : ""}" ${jobActive ? "disabled" : ""}>${exportDrawing ? "✕ Cancel draw" : "▱ Draw area"}</button>
        ${exportPolygon && !exportDrawing ? `<button id="surf-export-clear" style="${btnSmSty}" ${jobActive ? "disabled" : ""}>✕ Clear area</button>` : ""}
        <span class="small" style="color:var(--muted);font-size:10px;">${esc(areaLabel)}</span>
      </div>
      ${exportDrawing ? `<div class="small" style="color:#fbbf24;font-size:10px;margin-top:4px;">Click the map to add vertices · double-click to finish (min 3).</div>` : ""}
      ${maskHtml}
      <div style="margin-top:8px;">
        <button id="surf-export-go" style="${btnSty}${jobActive ? "opacity:0.5;cursor:default;" : ""}" ${jobActive || exportDrawing ? "disabled" : ""}>⬇ Export → DAM</button>
      </div>
      ${jobHtml}
    </div>`;
  }

  container.innerHTML = `
    <div style="border:1px solid rgba(148,163,184,0.18);border-radius:8px;overflow:hidden;">
      <div style="display:flex;align-items:center;padding:7px 10px;background:rgba(2,6,23,0.5);border-bottom:1px solid rgba(148,163,184,0.12);">
        <span class="small" style="font-weight:600;flex:1;">SURFACES${catalog.length?` (${catalog.length})`:""}${savePending?" — saving…":""}</span>
        <button id="surf-add-btn" style="${btnSty}margin-right:6px;">${addPickerOpen?"▾ Cancel":"+ Add"}</button>
        <button id="surf-refresh-btn" style="${btnSty}" title="Refresh">↻</button>
      </div>
      ${addPickerHtml}
      <div id="surf-catalog-list">${catalogRows}</div>
    </div>
    ${vizHtml}
    ${exportHtml}
  `;

  // Fix histogram width after layout settles — % width on position:relative can mis-compute before first paint.
  const histWrap = container.querySelector("#surf-hist-wrap");
  if (histWrap) {
    requestAnimationFrame(() => {
      const par = histWrap.parentElement;
      if (!par) return;
      const cs = getComputedStyle(par);
      const w = par.clientWidth - parseFloat(cs.paddingLeft || "0") - parseFloat(cs.paddingRight || "0");
      if (w > 0) histWrap.style.width = w + "px";
    });
  }

  bindEvents();
}

// ── Event binding ─────────────────────────────────────────────────────────────

function bindEvents() {
  if (!container) return;

  container.querySelector("#surf-add-btn")?.addEventListener("click", () => {
    addPickerOpen = !addPickerOpen;
    if (addPickerOpen) { loadAvailable(); loadMosaicAvailable(); }
    render();
  });
  container.querySelector("#surf-refresh-btn")?.addEventListener("click", () => loadCatalog());

  // Add the prebuilt 2D mosaic — rasterReady immediately, no build job to poll.
  container.querySelector("#surf-add-mosaic")?.addEventListener("click", () => {
    if (!mosaicAvailable?.tileCount || catalog.some(s => s.kind === "mosaic2d")) return;
    const settings = DEFAULT_SETTINGS();
    settings.displayUnit = "m"; // elevation COGs are meters
    settings.colormapName = "viridis";
    if (Number.isFinite(mosaicAvailable.stretchMin)) settings.rescaleMin = mosaicAvailable.stretchMin;
    if (Number.isFinite(mosaicAvailable.stretchMax)) settings.rescaleMax = mosaicAvailable.stretchMax;
    const entry = {
      id: uid(), kind: "mosaic2d", name: "2D LiDAR Mosaic", marinaCode: activeMarinaCode,
      s3Key: "", rasterPath: null, rasterReady: true,
      createdAt: new Date().toISOString(), settings,
    };
    catalog.push(entry);
    addPickerOpen = false;
    selectedId = entry.id;
    selectedIds.clear(); selectedIds.add(entry.id);
    app?.log?.("surface.mosaic2d.added", { marina: activeMarinaCode, tileCount: mosaicAvailable.tileCount });
    saveCatalog(); render();
    showSurface(entry.id);
  });

  // Add the prebuilt classification layer — rasterReady immediately, no build job.
  container.querySelector("#surf-add-mosaic-cls")?.addEventListener("click", () => {
    if (!mosaicAvailable?.attrs?.classification?.tileCount || catalog.some(s => isClsMosaic(s))) return;
    const settings = DEFAULT_SETTINGS();
    settings.hillshadeOverlay = false; // slope derivative — meaningless over class codes
    const entry = {
      id: uid(), kind: "mosaic2d", attr: "classification", name: "2D LiDAR Mosaic — Classification",
      marinaCode: activeMarinaCode, s3Key: "", rasterPath: null, rasterReady: true,
      createdAt: new Date().toISOString(), settings,
    };
    catalog.push(entry);
    addPickerOpen = false;
    selectedId = entry.id;
    selectedIds.clear(); selectedIds.add(entry.id);
    app?.log?.("surface.mosaic2dcls.added", { marina: activeMarinaCode, tileCount: mosaicAvailable.attrs.classification.tileCount, classes: mosaicAvailable.attrs.classification.classes });
    saveCatalog(); render();
    showSurface(entry.id);
  });

  // Add a class-filtered DEM (e.g. "Bathy Only") — same prebuilt-COG path as the plain
  // elevation mosaic, just pinned to whichever attr key the bulk generator registered it
  // under, with that key's own stretch (see mosaic2dSummary generalization server-side).
  container.querySelectorAll("[data-add-mosaic-product]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const key = btn.getAttribute("data-add-mosaic-product");
      const stats = mosaicAvailable?.attrs?.[key];
      if (!stats?.tileCount || catalog.some(s => s.kind === "mosaic2d" && s.attr === key)) return;
      const product = mosaicProducts[key];
      const settings = DEFAULT_SETTINGS();
      settings.displayUnit = "m";
      settings.colormapName = "viridis";
      if (Number.isFinite(stats.stretchMin)) settings.rescaleMin = stats.stretchMin;
      if (Number.isFinite(stats.stretchMax)) settings.rescaleMax = stats.stretchMax;
      const entry = {
        id: uid(), kind: "mosaic2d", attr: key, name: product?.label || key,
        marinaCode: activeMarinaCode, s3Key: "", rasterPath: null, rasterReady: true,
        createdAt: new Date().toISOString(), settings,
      };
      catalog.push(entry);
      addPickerOpen = false;
      selectedId = entry.id;
      selectedIds.clear(); selectedIds.add(entry.id);
      app?.log?.("surface.mosaic2dproduct.added", { marina: activeMarinaCode, attr: key, tileCount: stats.tileCount });
      saveCatalog(); render();
      showSurface(entry.id);
    });
  });

  // Add picker
  container.querySelectorAll("[data-surf-add-pick]").forEach(btn => {
    btn.addEventListener("click", async () => {
      const marina = btn.dataset.surfAddPick;
      const name = btn.dataset.surfAddName || marina;
      const s3Key = btn.dataset.surfAddS3key || "";
      const nativeUnit = btn.dataset.surfAddUnit || "ft";
      const pickRow = btn.closest("div")?.parentElement;
      const epsgInput = pickRow?.querySelector(`[data-surf-epsg-key="${s3Key}"]`);
      const resInput = pickRow?.querySelector(`[data-surf-res-key="${s3Key}"]`);
      const resolution = Number(resInput?.value);
      if (!Number.isFinite(resolution) || resolution < 0.05 || resolution > 5000) {
        resInput?.reportValidity?.();
        resInput?.focus?.();
        return;
      }
      const fillGapCells = Math.max(0, Math.min(20, Math.round(Number(pickRow?.querySelector(`[data-surf-fill-key="${s3Key}"]`)?.value) || 0)));
      const bandChecks = pickRow?.querySelectorAll(`[data-surf-band-key="${s3Key}"]:checked`) || [];
      const outputTypes = [...bandChecks].map(cb => cb.dataset.surfBandVal).filter(Boolean);
      const confirmed = await confirmLasCrs({ s3Key, marina: activeMarinaCode, sourceEpsg: epsgInput?.value?.trim() || null, fileName: name });
      if (confirmed === null) return;
      const sourceEpsg = confirmed || null;
      const settings = DEFAULT_SETTINGS();
      settings.displayUnit = nativeUnit === "m" ? "m" : "ft";
      const entry = { id: uid(), name, marinaCode: marina, s3Key, sourceEpsg: sourceEpsg || null, resolution, fillGapCells, rasterPath: null, rasterReady: false, bands: null, createdAt: new Date().toISOString(), settings, buildJob: { status: "running", progressPct: 0, message: "Queued…" } };
      catalog.push(entry);
      addPickerOpen = false;
      selectedId = entry.id;
      selectedIds.clear(); selectedIds.add(entry.id);
      try {
        await fetch("/api/surfaces/build", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ surfaceId: entry.id, s3Key, nativeUnit, sourceEpsg, resolution, fillGapCells, marina: activeMarinaCode, outputTypes, crsConfirmed: true }) });
      } catch {}
      app?.log?.("surface.build.start", { surfaceId: entry.id, name, s3Key, resolution, fillGapCells, sourceEpsg: sourceEpsg || null, outputTypes });
      startBuildPoll(entry.id);
      saveCatalog(); render();
    });
  });

  // Grid statistic (multi-band surfaces only) — switching bands changes the value range
  // entirely (e.g. Mean elevation vs. Count), so the old rescale stretch is meaningless for
  // the new band; clearing it lets fetchHistogram's existing auto-rescale-if-null logic pick
  // fresh bounds, same as it does on first load.
  container.querySelector("#surf-display-band")?.addEventListener("change", (e) => {
    const s = getSelected();
    if (!s) return;
    s.settings.displayBand = e.target.value;
    s.settings.rescaleMin = null;
    s.settings.rescaleMax = null;
    saveCatalog();
    if (visibleIds.has(s.id)) { hideSurface(s.id); showSurface(s.id); }
    fetchHistogram(s.id);
    render();
  });

  // Find Flat Regions (beta) — dock-candidate detector form
  container.querySelector("#surf-flat-toggle")?.addEventListener("click", () => {
    const s = getSelected();
    if (!s) return;
    getSurfaceRuntime(s.id).flatRegionsOpen = true;
    render();
  });
  container.querySelector("#surf-flat-cancel")?.addEventListener("click", () => {
    const s = getSelected();
    if (!s) return;
    getSurfaceRuntime(s.id).flatRegionsOpen = false;
    render();
  });
  container.querySelector("#surf-flat-clear")?.addEventListener("click", () => {
    const s = getSelected();
    if (s) clearFlatRegions(s.id);
  });
  container.querySelector("#surf-flat-go")?.addEventListener("click", () => {
    const s = getSelected();
    if (!s) return;
    const rt = getSurfaceRuntime(s.id);
    const stdevMax = Number(container.querySelector("#surf-flat-stdev")?.value);
    const minAreaSqFt = Number(container.querySelector("#surf-flat-area")?.value);
    const minElongation = Number(container.querySelector("#surf-flat-elong")?.value);
    if (!Number.isFinite(stdevMax) || stdevMax <= 0 || !Number.isFinite(minAreaSqFt) || minAreaSqFt < 1 || !Number.isFinite(minElongation) || minElongation < 1) return;
    rt.flatRegionsParams = { stdevMax, minAreaSqFt, minElongation };
    runIsolateFlat(s.id);
  });

  // Zoom Extents
  container.querySelectorAll("[data-surf-zoom]").forEach(btn => {
    btn.addEventListener("click", () => {
      const s = catalog.find(c => c.id === btn.dataset.surfZoom);
      if (s) zoomToSurfaceExtent(s);
    });
  });

  // Open in 3D (proof-of-concept)
  container.querySelectorAll("[data-surf-open3d]").forEach(btn => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.surfOpen3d;
      const s = catalog.find(c => c.id === id);
      if (!s) return;
      if (s.open3dStatus === "ready" && s.open3dUrl) { window.open(s.open3dUrl, "_blank"); if (s.open3dBlocked) { s.open3dBlocked = false; render(); } return; }
      if (s.open3dStatus && s.open3dStatus !== "failed") return; // already in flight
      s.open3dStatus = "queued";
      render();
      pollOpen3d(id);
    });
  });

  // Rebuild form
  container.querySelector("#surf-rebuild-toggle")?.addEventListener("click", () => { rebuildOpen = true; render(); });
  container.querySelector("#surf-rebuild-cancel")?.addEventListener("click", () => { rebuildOpen = false; render(); });
  container.querySelector("#surf-rebuild-go")?.addEventListener("click", async () => {
    const sel = getSelected();
    if (!sel) return;
    let epsg = container.querySelector("#surf-rebuild-epsg")?.value?.trim() || null;
    const resInput = container.querySelector("#surf-rebuild-res");
    const res = Number(resInput?.value);
    if (!Number.isFinite(res) || res < 0.05 || res > 5000) {
      resInput?.reportValidity?.();
      resInput?.focus?.();
      return;
    }
    const fillGapCells = Math.max(0, Math.min(20, Math.round(Number(container.querySelector("#surf-rebuild-fill")?.value) || 0)));
    const outputTypes = [...container.querySelectorAll("[id^='surf-rebuild-band-']:checked")].map(cb => cb.dataset.surfBandVal).filter(Boolean);
    const confirmed = await confirmLasCrs({ s3Key: sel.s3Key, marina: activeMarinaCode, sourceEpsg: epsg, fileName: sel.name });
    if (confirmed === null) return;
    epsg = confirmed || null;
    sel.sourceEpsg = epsg || null;
    sel.resolution = res;
    sel.fillGapCells = fillGapCells;
    sel.rasterReady = false;
    sel.bands = null;
    sel.buildJob = { status: "running", progressPct: 0, message: "Queued…" };
    rebuildOpen = false;
    hideSurface(sel.id);
    stopBuildPoll(sel.id);
    try {
      await fetch("/api/surfaces/build", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ surfaceId: sel.id, s3Key: sel.s3Key, nativeUnit: sel.settings.displayUnit || "ft", sourceEpsg: epsg, resolution: res, fillGapCells, marina: activeMarinaCode, outputTypes, crsConfirmed: true }) });
    } catch {}
    app?.log?.("surface.rebuild.start", { surfaceId: sel.id, name: sel.name, resolution: res, fillGapCells, sourceEpsg: epsg || null, outputTypes });
    startBuildPoll(sel.id);
    saveCatalog(); render();
  });

  // Visibility
  container.querySelectorAll("[data-surf-vis]").forEach(cb => {
    cb.addEventListener("change", e => { e.target.checked ? showSurface(cb.dataset.surfVis) : hideSurface(cb.dataset.surfVis); });
  });

  // Per-row opacity slider
  container.querySelectorAll("[data-surf-opacity]").forEach(slider => {
    const id = slider.dataset.surfOpacity;
    slider.addEventListener("mousedown", () => {
      const row = slider.closest("[data-surf-row]");
      if (!row) return;
      row.draggable = false;
      const restore = () => { row.draggable = true; document.removeEventListener("mouseup", restore); };
      document.addEventListener("mouseup", restore);
    });
    slider.addEventListener("input", e => {
      const s = catalog.find(c => c.id === id);
      if (!s) return;
      s.settings.tileOpacity = Number(e.target.value) / 100;
      const lbl = container.querySelector(`[data-surf-opacity-label="${id}"]`);
      if (lbl) lbl.textContent = `${Math.round(s.settings.tileOpacity*100)}%`;
      const rt = surfaceRuntimes.get(id);
      if (rt?.tileLayer) rt.tileLayer.setOpacity(s.settings.tileOpacity);
      if (rt?.hillshadeLayer) rt.hillshadeLayer.setOpacity(s.settings.tileOpacity);
    });
    slider.addEventListener("change", () => saveCatalog());
  });

  // Select for viz controls (shift = range, ctrl/meta = toggle, plain = exclusive)
  container.querySelectorAll("[data-surf-select]").forEach(el => {
    el.addEventListener("click", e => {
      const id = el.dataset.surfSelect;
      rebuildOpen = false;
      if (e.ctrlKey || e.metaKey) {
        if (selectedIds.has(id)) {
          selectedIds.delete(id);
          if (selectedId === id) selectedId = [...selectedIds][selectedIds.size - 1] ?? null;
        } else {
          selectedIds.add(id);
          selectedId = id;
        }
      } else if (e.shiftKey && selectedId) {
        const ids = catalog.map(s => s.id);
        const lo = Math.min(ids.indexOf(selectedId), ids.indexOf(id));
        const hi = Math.max(ids.indexOf(selectedId), ids.indexOf(id));
        for (let i = lo; i <= hi; i++) selectedIds.add(ids[i]);
        selectedId = id;
      } else {
        if (selectedIds.size === 1 && selectedIds.has(id)) {
          selectedIds.clear(); selectedId = null;
        } else {
          selectedIds.clear(); selectedIds.add(id); selectedId = id;
        }
      }
      render();
    });
  });

  // Rename
  container.querySelectorAll("[data-surf-rename]").forEach(btn => {
    btn.addEventListener("click", () => {
      const s = catalog.find(c => c.id === btn.dataset.surfRename);
      if (!s) return;
      const n = prompt("Rename surface:", s.name);
      if (n?.trim()) { s.name = n.trim(); saveCatalog(); render(); }
    });
  });

  // Remove
  container.querySelectorAll("[data-surf-remove]").forEach(btn => {
    btn.addEventListener("click", () => {
      if (!confirm("Remove this surface from the catalog?")) return;
      const id = btn.dataset.surfRemove;
      stopBuildPoll(id); hideSurface(id); surfaceRuntimes.delete(id);
      catalog = catalog.filter(s => s.id !== id);
      selectedIds.delete(id);
      if (selectedId === id) selectedId = [...selectedIds][selectedIds.size - 1] ?? null;
      saveCatalog(); render();
    });
  });

  // Retry build
  container.querySelector("#surf-retry-build")?.addEventListener("click", async () => {
    const s = getSelected();
    if (!s) return;
    const epsgInput = container.querySelector("#surf-retry-epsg");
    const confirmed = await confirmLasCrs({ s3Key: s.s3Key, marina: activeMarinaCode, sourceEpsg: epsgInput?.value?.trim() || null, fileName: s.name });
    if (confirmed === null) return;
    s.sourceEpsg = confirmed || null;
    delete s.crsCheck;
    s.buildJob = { status: "running", progressPct: 0, message: "Queued…" };
    try {
      await fetch("/api/surfaces/build", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ surfaceId: s.id, s3Key: s.s3Key, nativeUnit: s.settings.displayUnit, sourceEpsg: s.sourceEpsg || null, resolution: s.resolution || undefined, marina: activeMarinaCode, crsConfirmed: true }) });
    } catch {}
    startBuildPoll(s.id); saveCatalog(); render();
  });

  // Drag-and-drop reorder
  container.querySelectorAll("[data-surf-row]").forEach(row => {
    row.addEventListener("dragstart", e => { dragSrcId = row.dataset.surfRow; e.dataTransfer.effectAllowed = "move"; });
    row.addEventListener("dragover", e => { e.preventDefault(); row.style.outline = "1px solid rgba(56,189,248,0.4)"; });
    row.addEventListener("dragleave", () => { row.style.outline = ""; });
    row.addEventListener("drop", e => {
      e.preventDefault(); row.style.outline = "";
      const dropId = row.dataset.surfRow;
      if (!dragSrcId || dragSrcId === dropId) return;
      const fi = catalog.findIndex(s => s.id === dragSrcId);
      const ti = catalog.findIndex(s => s.id === dropId);
      if (fi < 0 || ti < 0) return;
      const [moved] = catalog.splice(fi, 1);
      catalog.splice(ti, 0, moved);
      reapplyZIndices(); saveCatalog(); render();
    });
    row.addEventListener("dragend", () => { dragSrcId = null; });
  });

  // ── Viz controls (raster-ready surfaces only) ────────────────────────────
  const sel = getSelected();
  if (!sel?.rasterReady) return;
  const st = sel.settings;
  const rt = surfaceRuntimes.get(sel.id);
  function settingChanged() { saveCatalog(); for (const id of selectedIds) { if (visibleIds.has(id)) refreshSurface(id); } }

  // Classification panel: per-class visibility/color → discrete colormap → layer refresh.
  if (isClsMosaic(sel) && selectedIds.size === 1) {
    if (!st.classStyles || typeof st.classStyles !== "object") st.classStyles = {};
    container.querySelectorAll("[data-surf-class-vis]").forEach(cb => {
      cb.addEventListener("change", e => {
        const id = cb.dataset.surfClassVis;
        st.classStyles[id] = { ...(st.classStyles[id] || {}), visible: e.target.checked };
        settingChanged(); render();
      });
    });
    container.querySelectorAll("[data-surf-class-color]").forEach(inp => {
      const id = inp.dataset.surfClassColor;
      inp.addEventListener("input", e => {
        // Update swatch directly — render() here would close the native color picker.
        st.classStyles[id] = { ...(st.classStyles[id] || {}), color: e.target.value };
        const sw = container.querySelector(`[data-surf-class-swatch="${id}"]`);
        if (sw) sw.style.background = e.target.value;
      });
      inp.addEventListener("change", () => settingChanged());
    });
    container.querySelector("#surf-class-reset")?.addEventListener("click", () => {
      st.classStyles = {};
      settingChanged(); render();
    });
    return; // continuous controls below don't exist in this panel
  }

  // Colormap dropdown
  if (closeCmDropdown) { document.removeEventListener("mousedown", closeCmDropdown); closeCmDropdown = null; }
  container.querySelector("#surf-cm-trigger")?.addEventListener("click", e => {
    e.stopPropagation();
    cmPickerOpen = !cmPickerOpen;
    render();
    if (cmPickerOpen) {
      closeCmDropdown = ev => {
        const dropdown = container.querySelector("#surf-cm-dropdown");
        const trigger = container.querySelector("#surf-cm-trigger");
        if (dropdown?.contains(ev.target) || trigger?.contains(ev.target)) return;
        cmPickerOpen = false;
        document.removeEventListener("mousedown", closeCmDropdown);
        closeCmDropdown = null;
        render();
      };
      setTimeout(() => document.addEventListener("mousedown", closeCmDropdown), 0);
    }
  });
  container.querySelectorAll("[data-surf-cm]").forEach(btn => {
    btn.addEventListener("click", () => {
      const newCm = btn.dataset.surfCm;
      applyToAllSelected(s => { s.settings.colormapName = newCm; });
      cmPickerOpen = false;
      if (closeCmDropdown) { document.removeEventListener("mousedown", closeCmDropdown); closeCmDropdown = null; }
      // A histogram zoom window fitted to the old colormap's rescale range doesn't carry
      // meaning across a colormap switch (custom bins have their own divider domain).
      if (rt) { rt.histViewRange = null; rt.histViewData = null; }
      settingChanged(); render();
    });
  });

  // Histogram zoom — mouse wheel over the histogram narrows/widens the view centered on
  // the cursor, so a tight band like a dock elevation isn't squeezed into one bin of the
  // whole-raster histogram. The reset button only appears once zoomed in.
  container.querySelector("#surf-hist-wrap")?.addEventListener("wheel", (e) => {
    if (selectedIds.size === 1) onHistWheel(selectedId, e);
  }, { passive: false });
  container.querySelector("#surf-hist-zoom-reset")?.addEventListener("click", () => {
    if (selectedIds.size === 1) resetHistZoom(selectedId);
  });


  // Histogram drag handles (standard colormap mode — min/max rescale bars)
  function startHistDrag(which) {
    return function(e) {
      e.preventDefault();
      const histWrap = container.querySelector("#surf-hist-wrap");
      if (!histWrap) return;
      const parentRect = histWrap.getBoundingClientRect();
      const dragZoomed = selectedIds.size === 1 && st.colormapName !== "solid" && !!rt?.histViewRange;
      const activeHistData = dragZoomed && rt?.histViewData ? rt.histViewData : (lastMergedHistData ?? rt?.histData);
      const histLo = dragZoomed ? rt.histViewRange[0] : (activeHistData?.[1]?.[0] ?? (st.rescaleMin ?? -50));
      const histHi = dragZoomed ? rt.histViewRange[1] : (activeHistData?.[1]?.[activeHistData[1].length-1] ?? (st.rescaleMax ?? 0));
      const range = histHi - histLo || 1;
      function onMove(ev) {
        const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
        const pct = Math.max(0, Math.min(100, (clientX - parentRect.left) / parentRect.width * 100));
        const val = Math.round((histLo + pct/100*range)*10)/10;
        if (which === "min") {
          st.rescaleMin = Math.min(val, (st.rescaleMax ?? histHi) - 0.1);
          const p = Math.max(0, Math.min(100, (st.rescaleMin-histLo)/range*100));
          const bar = container.querySelector("#surf-hist-bar-min");
          const lbl = container.querySelector("#surf-hist-label-min");
          if (bar) bar.style.left = `${p.toFixed(2)}%`;
          if (lbl) lbl.textContent = `${st.rescaleMin.toFixed(1)} ${st.displayUnit}`;
        } else {
          st.rescaleMax = Math.max(val, (st.rescaleMin ?? histLo) + 0.1);
          const p = Math.max(0, Math.min(100, (st.rescaleMax-histLo)/range*100));
          const bar = container.querySelector("#surf-hist-bar-max");
          const lbl = container.querySelector("#surf-hist-label-max");
          if (bar) bar.style.left = `${p.toFixed(2)}%`;
          if (lbl) lbl.textContent = `${st.rescaleMax.toFixed(1)} ${st.displayUnit}`;
        }
        const svgEl = container.querySelector("#surf-hist-svg");
        if (svgEl && activeHistData) svgEl.innerHTML = generateHistogramBars(activeHistData[0], activeHistData[1], sel);
      }
      function onUp() {
        document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp);
        document.removeEventListener("touchmove", onMove); document.removeEventListener("touchend", onUp);
        const min = st.rescaleMin, max = st.rescaleMax;
        applyToAllSelected(s => { s.settings.rescaleMin = min; s.settings.rescaleMax = max; });
        settingChanged();
      }
      document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp);
      document.addEventListener("touchmove", onMove, { passive:false }); document.addEventListener("touchend", onUp);
    };
  }
  container.querySelector("#surf-hist-bar-min")?.addEventListener("mousedown", startHistDrag("min"));
  container.querySelector("#surf-hist-bar-max")?.addEventListener("mousedown", startHistDrag("max"));
  container.querySelector("#surf-hist-bar-min")?.addEventListener("touchstart", startHistDrag("min"), { passive:false });
  container.querySelector("#surf-hist-bar-max")?.addEventListener("touchstart", startHistDrag("max"), { passive:false });

  // Custom colormap: draggable divider bars
  const solidHistData = lastMergedHistData ?? rt?.histData;
  if (st.colormapName === "solid" && solidHistData) {
    const histWrap = container.querySelector("#surf-hist-wrap");
    const lo = solidHistData[1][0], hi = solidHistData[1][solidHistData[1].length-1], range2 = hi - lo || 1;
    const divs = ensureCustomDividers(sel, lo, hi);
    divs.forEach((_, idx) => {
      const bar = container.querySelector(`#surf-hist-divider-${idx}`);
      if (!bar) return;
      function makeDivDrag(startEvt) {
        startEvt.preventDefault();
        const rect = histWrap.getBoundingClientRect();
        function onMove(ev) {
          const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
          const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
          const val = Math.round((lo + pct * range2) * 10) / 10;
          const sorted = [...st.customDividers].sort((a, b) => a - b);
          const prev = idx === 0 ? lo + 0.1 : sorted[idx-1] + 0.1;
          const next = idx === sorted.length-1 ? hi - 0.1 : sorted[idx+1] - 0.1;
          sorted[idx] = Math.max(prev, Math.min(next, val));
          st.customDividers = sorted;
          // update bar + label positions live
          sorted.forEach((v, i) => {
            const p = Math.max(0, Math.min(100, (v - lo) / range2 * 100));
            const b = container.querySelector(`#surf-hist-divider-${i}`);
            const l = container.querySelector(`#surf-hist-divider-label-${i}`);
            if (b) b.style.left = `${p.toFixed(2)}%`;
            if (l) { l.style.left = barLabelLeft(p); l.style.transform = barLabelTransform(p); l.textContent = `${v.toFixed(1)} ${st.displayUnit}`; }
          });
          // update table from-cells and to-inputs
          const recomputedDivs = [...st.customDividers].sort((a,b) => a-b);
          for (let i = 0; i < st.customBins; i++) {
            const fromEl = container.querySelector(`#surf-custom-from-${i}`);
            if (fromEl) fromEl.textContent = (i === 0 ? lo : recomputedDivs[i-1]).toFixed(1);
            if (i < st.customBins - 1) {
              const toInput = container.querySelector(`#surf-custom-to-${i}`);
              if (toInput && document.activeElement !== toInput) toInput.value = recomputedDivs[i].toFixed(1);
            }
          }
          const svgEl = container.querySelector("#surf-hist-svg");
          if (svgEl) svgEl.innerHTML = generateHistogramBars(solidHistData[0], solidHistData[1], sel);
          refreshSurface(sel.id);
        }
        function onUp() {
          document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp);
          document.removeEventListener("touchmove", onMove); document.removeEventListener("touchend", onUp);
          const finalDivs = [...st.customDividers];
          applyToAllSelected(s => { if (s.id !== sel.id) s.settings.customDividers = [...finalDivs]; });
          settingChanged();
        }
        document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp);
        document.addEventListener("touchmove", onMove, { passive:false }); document.addEventListener("touchend", onUp);
      }
      bar.style.pointerEvents = "all";
      bar.addEventListener("mousedown", makeDivDrag);
      bar.addEventListener("touchstart", makeDivDrag, { passive:false });
    });
  }

  // Custom colormap: bins spinner
  container.querySelector("#surf-custom-bins")?.addEventListener("change", e => {
    const newBins = Math.max(2, Math.min(8, parseInt(e.target.value, 10) || st.customBins));
    applyToAllSelected(s => {
      const sRt = surfaceRuntimes.get(s.id);
      const sLo = sRt?.histData?.[1]?.[0] ?? -50;
      const sHi = sRt?.histData?.[1]?.[sRt.histData[1].length - 1] ?? 0;
      ensureCustomDividers(s, sLo, sHi);
      const oldDivs = [...s.settings.customDividers].sort((a, b) => a - b);
      const newDivCount = newBins - 1;
      if (newDivCount > oldDivs.length) {
        const lastDiv = oldDivs[oldDivs.length - 1] ?? sLo;
        s.settings.customDividers = [...oldDivs, (lastDiv + sHi) / 2];
      } else {
        s.settings.customDividers = oldDivs.slice(0, newDivCount);
      }
      s.settings.customBins = newBins;
    });
    settingChanged(); render();
  });

  // Custom colormap: color pickers + transparency + to-inputs
  for (let i = 0; i < st.customBins; i++) {
    const idx = i;
    container.querySelector(`#surf-custom-picker-${idx}`)?.addEventListener("input", e => {
      const rgb = hexToRgb(e.target.value);
      applyToAllSelected(s => { s.settings.customColors[idx] = [...rgb]; });
      const sw = container.querySelector(`#surf-custom-swatch-${idx}`);
      if (sw) sw.style.background = e.target.value;
      const svgEl = container.querySelector("#surf-hist-svg");
      const ahd = lastMergedHistData ?? rt?.histData;
      if (svgEl && ahd) svgEl.innerHTML = generateHistogramBars(ahd[0], ahd[1], sel);
      for (const id of selectedIds) if (visibleIds.has(id)) refreshSurface(id);
    });
    container.querySelector(`#surf-custom-picker-${idx}`)?.addEventListener("change", () => saveCatalog());
    container.querySelector(`#surf-custom-transp-${idx}`)?.addEventListener("click", () => {
      const newTransp = !st.customTransparent[idx];
      applyToAllSelected(s => { s.settings.customTransparent[idx] = newTransp; });
      settingChanged(); render();
    });
    container.querySelector(`#surf-custom-to-${idx}`)?.addEventListener("change", e => {
      const newVal = parseFloat(e.target.value);
      if (isNaN(newVal)) return;
      applyToAllSelected(s => {
        const sRt = surfaceRuntimes.get(s.id);
        const sLo = sRt?.histData?.[1]?.[0] ?? -50;
        const sHi = sRt?.histData?.[1]?.[sRt.histData[1].length - 1] ?? 0;
        ensureCustomDividers(s, sLo, sHi);
        const sorted = [...s.settings.customDividers].sort((a, b) => a - b);
        sorted[idx] = newVal;
        s.settings.customDividers = sorted;
      });
      settingChanged(); render();
    });
  }

  // Underflow/overflow
  container.querySelector("#surf-underflow-picker")?.addEventListener("input", e => {
    const rgb = hexToRgb(e.target.value);
    applyToAllSelected(s => { s.settings.underflowColor = [...rgb]; });
    // Update swatch directly — avoid render() which would close the native picker
    const swInner = e.target.previousElementSibling;
    if (swInner && !st.underflowTransparent) swInner.style.background = e.target.value;
    const svgEl = container.querySelector("#surf-hist-svg");
    const ahd = lastMergedHistData ?? rt?.histData;
    if (svgEl && ahd) svgEl.innerHTML = generateHistogramBars(ahd[0], ahd[1], sel);
    for (const id of selectedIds) if (visibleIds.has(id)) refreshSurface(id);
  });
  container.querySelector("#surf-underflow-picker")?.addEventListener("change", () => saveCatalog());
  container.querySelector("#surf-underflow-transp")?.addEventListener("click", () => {
    const newVal = !st.underflowTransparent;
    applyToAllSelected(s => { s.settings.underflowTransparent = newVal; });
    settingChanged(); render();
  });
  container.querySelector("#surf-overflow-picker")?.addEventListener("input", e => {
    const rgb = hexToRgb(e.target.value);
    applyToAllSelected(s => { s.settings.overflowColor = [...rgb]; });
    // Update swatch directly — avoid render() which would close the native picker
    const swInner = e.target.previousElementSibling;
    if (swInner && !st.overflowTransparent) swInner.style.background = e.target.value;
    const svgEl = container.querySelector("#surf-hist-svg");
    const ahd = lastMergedHistData ?? rt?.histData;
    if (svgEl && ahd) svgEl.innerHTML = generateHistogramBars(ahd[0], ahd[1], sel);
    for (const id of selectedIds) if (visibleIds.has(id)) refreshSurface(id);
  });
  container.querySelector("#surf-overflow-picker")?.addEventListener("change", () => saveCatalog());
  container.querySelector("#surf-overflow-transp")?.addEventListener("click", () => {
    const newVal = !st.overflowTransparent;
    applyToAllSelected(s => { s.settings.overflowTransparent = newVal; });
    settingChanged(); render();
  });

  // Hillshade toggle
  container.querySelector("#surf-hillshade")?.addEventListener("change", e => {
    const checked = e.target.checked;
    applyToAllSelected(s => {
      s.settings.hillshadeOverlay = checked;
      const sRt = surfaceRuntimes.get(s.id);
      if (!sRt) return;
      if (checked && visibleIds.has(s.id) && !sRt.hillshadeLayer) {
        sRt.hillshadeLayer = createHillshadeLayer(s, sRt, app.map.instance, sRt.colormapId ?? null);
        bindTileProgress(sRt, sRt.hillshadeLayer);
      } else if (!checked && sRt.hillshadeLayer) {
        try { sRt.hillshadeLayer.remove(); } catch {} sRt.hillshadeLayer = null;
      }
    });
    settingChanged(); render();
  });

  // Sun picker
  const sunSvg = container.querySelector("#surf-sun-svg");
  if (sunSvg) {
    function sunPickFromEvent(e) {
      const rect = sunSvg.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = clientX - rect.left - 35; // center of 70px svg
      const dy = clientY - rect.top - 35;
      return {
        azimuth: Math.round(((Math.atan2(dx,-dy)*180/Math.PI)+360)%360),
        altitude: Math.round((Math.min(31.5, Math.sqrt(dx*dx+dy*dy))/31.5)*90),
      };
    }
    function moveSunDot(az, alt) {
      const r2 = (alt/90)*36;
      const azRad = az*Math.PI/180;
      const cx = (40+r2*Math.sin(azRad)).toFixed(1);
      const cy = (40-r2*Math.cos(azRad)).toFixed(1);
      const dot = container.querySelector("#surf-sun-dot"); if (dot) { dot.setAttribute("cx",cx); dot.setAttribute("cy",cy); }
      const glow = container.querySelector("#surf-sun-glow"); if (glow) { glow.setAttribute("cx",cx); glow.setAttribute("cy",cy); }
      const lbl = container.querySelector("#surf-sun-label"); if (lbl) lbl.textContent = `Az ${az}° · Alt ${alt}°`;
    }
    function startSunDrag(e) {
      e.preventDefault();
      const { azimuth, altitude } = sunPickFromEvent(e);
      st.hillshadeAzimuth = azimuth; st.hillshadeAltitude = altitude; moveSunDot(azimuth, altitude);
      function onMove(ev) {
        if (ev.touches) ev.preventDefault();
        const { azimuth:az, altitude:alt } = sunPickFromEvent(ev);
        st.hillshadeAzimuth = az; st.hillshadeAltitude = alt; moveSunDot(az, alt);
      }
      function onUp() {
        document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp);
        document.removeEventListener("touchmove", onMove); document.removeEventListener("touchend", onUp);
        const az = st.hillshadeAzimuth, alt = st.hillshadeAltitude;
        applyToAllSelected(s => { s.settings.hillshadeAzimuth = az; s.settings.hillshadeAltitude = alt; });
        for (const id of selectedIds) {
          if (!visibleIds.has(id)) continue;
          const sRt = surfaceRuntimes.get(id);
          const s = catalog.find(c => c.id === id);
          if (!sRt || !s?.settings.hillshadeOverlay) continue;
          if (sRt.hillshadeLayer) { try { sRt.hillshadeLayer.remove(); } catch {} sRt.hillshadeLayer = null; }
          sRt.hillshadeLayer = createHillshadeLayer(s, sRt, app.map.instance, sRt.colormapId ?? null);
          bindTileProgress(sRt, sRt.hillshadeLayer);
        }
        saveCatalog();
      }
      document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp);
      document.addEventListener("touchmove", onMove, { passive:false }); document.addEventListener("touchend", onUp);
    }
    sunSvg.addEventListener("mousedown", startSunDrag);
    sunSvg.addEventListener("touchstart", startSunDrag, { passive:false });
  }

  // Gamma
  container.querySelector("#surf-gamma")?.addEventListener("input", e => {
    st.tileGamma = Number(e.target.value)/100;
    const lbl = container.querySelector("#surf-gamma-val"); if (lbl) lbl.textContent = st.tileGamma.toFixed(2);
    if (rt?.paneEl) rt.paneEl.style.filter = buildPaneFilter(sel);
  });
  container.querySelector("#surf-gamma")?.addEventListener("change", e => {
    const gamma = Number(e.target.value)/100;
    applyToAllSelected(s => { s.settings.tileGamma = gamma; const sRt = surfaceRuntimes.get(s.id); if (sRt?.paneEl) sRt.paneEl.style.filter = buildPaneFilter(s); });
    saveCatalog();
  });

  // Saturation
  container.querySelector("#surf-saturation")?.addEventListener("input", e => {
    st.tileSaturation = Number(e.target.value)/100;
    const lbl = container.querySelector("#surf-saturation-val"); if (lbl) lbl.textContent = st.tileSaturation.toFixed(2);
    if (rt?.paneEl) rt.paneEl.style.filter = buildPaneFilter(sel);
  });
  container.querySelector("#surf-saturation")?.addEventListener("change", e => {
    const sat = Number(e.target.value)/100;
    applyToAllSelected(s => { s.settings.tileSaturation = sat; const sRt = surfaceRuntimes.get(s.id); if (sRt?.paneEl) sRt.paneEl.style.filter = buildPaneFilter(s); });
    saveCatalog();
  });

  // Contrast
  container.querySelector("#surf-contrast")?.addEventListener("input", e => {
    st.tileContrast = Number(e.target.value)/100;
    const lbl = container.querySelector("#surf-contrast-val"); if (lbl) lbl.textContent = st.tileContrast.toFixed(2);
    if (rt?.paneEl) rt.paneEl.style.filter = buildPaneFilter(sel);
  });
  container.querySelector("#surf-contrast")?.addEventListener("change", e => {
    const con = Number(e.target.value)/100;
    applyToAllSelected(s => { s.settings.tileContrast = con; const sRt = surfaceRuntimes.get(s.id); if (sRt?.paneEl) sRt.paneEl.style.filter = buildPaneFilter(s); });
    saveCatalog();
  });

  // Z-factor (hillshade vertical exaggeration) — rebuilds hillshade tiles on change
  container.querySelector("#surf-zfactor")?.addEventListener("input", e => {
    st.tileZFactor = Number(e.target.value)/10;
    const lbl = container.querySelector("#surf-zfactor-val"); if (lbl) lbl.textContent = st.tileZFactor.toFixed(1)+"×";
  });
  container.querySelector("#surf-zfactor")?.addEventListener("change", e => {
    const zf = Number(e.target.value)/10;
    applyToAllSelected(s => {
      s.settings.tileZFactor = zf;
      const sRt = surfaceRuntimes.get(s.id);
      if (sRt?.hillshadeLayer && s.settings.hillshadeOverlay && visibleIds.has(s.id)) {
        try { sRt.hillshadeLayer.remove(); } catch {}
        sRt.hillshadeLayer = createHillshadeLayer(s, sRt, app.map.instance, sRt.colormapId ?? null);
        bindTileProgress(sRt, sRt.hillshadeLayer);
      }
    });
    saveCatalog();
  });

  // GeoTIFF export
  container.querySelector("#surf-export-draw")?.addEventListener("click", () => {
    if (exportDrawing) stopExportDraw(); else startExportDraw();
  });
  container.querySelector("#surf-export-clear")?.addEventListener("click", () => {
    clearExportPolygon(); render();
  });
  container.querySelector("#surf-export-mask")?.addEventListener("change", (e) => {
    exportApplyMask = !!e.target.checked;
  });
  container.querySelector("#surf-export-go")?.addEventListener("click", () => {
    const s = getSelected();
    if (s) startExport(s);
  });

  // Dock centerline
  container.querySelector("#surf-dockline-draw")?.addEventListener("click", () => {
    if (dockLineActive) stopDockLineDraw(); else startDockLineDraw();
  });
  container.querySelector("#surf-dockline-width")?.addEventListener("change", (e) => {
    dockLineWidth = Math.max(0.2, Number(e.target.value) || 2);
  });
  container.querySelector("#surf-dockline-tolerance")?.addEventListener("change", (e) => {
    dockLineTolerance = Math.max(0.05, Number(e.target.value) || 0.5);
  });
  container.querySelector("#surf-dockline-clear")?.addEventListener("click", () => {
    const s = getSelected();
    if (s) clearDockLineSamples(s);
  });
  container.querySelectorAll("[data-dockline-remove]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const s = getSelected();
      if (s) removeDockLineSample(s, btn.dataset.docklineRemove);
    });
  });

  // Reset adjustments
  container.querySelector("#surf-adj-reset")?.addEventListener("click", () => {
    applyToAllSelected(s => {
      s.settings.tileZFactor    = 1.0;
      s.settings.tileGamma      = 1.0;
      s.settings.tileSaturation = 1.0;
      s.settings.tileContrast   = 1.0;
      const sRt = surfaceRuntimes.get(s.id);
      if (sRt?.paneEl) sRt.paneEl.style.filter = "";
      if (sRt?.hillshadeLayer && s.settings.hillshadeOverlay && visibleIds.has(s.id)) {
        try { sRt.hillshadeLayer.remove(); } catch {}
        sRt.hillshadeLayer = createHillshadeLayer(s, sRt, app.map.instance, sRt.colormapId ?? null);
        bindTileProgress(sRt, sRt.hillshadeLayer);
      }
    });
    saveCatalog(); render();
  });
}

// ── Tool definition ───────────────────────────────────────────────────────────

function onProjectChanged(e) {
  const code = String(e.detail?.projectId || "").trim().toUpperCase();
  if (code === activeMarinaCode) return;
  activeMarinaCode = code;
  // The old marina's surface catalog entries (and their surfaceIds) are gone the moment we
  // reassign `catalog` below -- any tile/hillshade layers still on the map at that point would
  // never get cleaned up otherwise, since hiding a surface normally goes through the catalog's
  // own toggle UI, which no longer has a matching entry to drive it. Mirrors the same cleanup
  // destroy() does, minus tearing down the tool itself.
  for (const [id] of surfaceRuntimes) hideSurface(id);
  surfaceRuntimes.clear();
  visibleIds.clear();
  catalog = [];
  mosaicAvailable = null;
  selectedId = null;
  selectedIds.clear();
  resetExportState();
  stopDockLineDraw();
  dockLineSamplesByS.clear();
  render();
  loadCatalog();
  loadMosaicAvailable();
}

export const surfacesTool = {
  id: "surfaces",
  name: "Surfaces",
  init(appContext) {
    app = appContext;
    document.addEventListener("hydrotools:project-changed", onProjectChanged);
    startCatalogSync();
  },
  destroy() {
    document.removeEventListener("hydrotools:project-changed", onProjectChanged);
    stopCatalogSync();
    resetExportState();
    stopDockLineDraw();
    dockLineSamplesByS.clear();
    for (const id of [...buildPolls.keys()]) stopBuildPoll(id);
    for (const [id] of surfaceRuntimes) hideSurface(id);
    surfaceRuntimes.clear();
    visibleIds.clear();
    if (closeCmDropdown) { document.removeEventListener("mousedown", closeCmDropdown); closeCmDropdown = null; }
    cmPickerOpen = false;
    catalog = [];
    activeMarinaCode = "";
    selectedId = null;
    selectedIds.clear();
    container = null;
    app = null;
  },
  renderPanel(_appContext, panelEl) {
    container = panelEl;
    render();
  },
};
